import React from 'react';
import {
  Document,
  Image,
  Link,
  Page,
  Path,
  Rect,
  StyleSheet,
  Svg,
  Text,
  View,
} from '@react-pdf/renderer';
import i18n from 'src/i18n';
import registerFonts from 'src/utils/fonts';
import {
  DEFAULT_REPORT_ACCENT_COLOR,
  getReportSectionTitle,
  getReportSectionDescription,
  getReportTypeLabel,
} from './config';
import {
  formatTargetAudience,
  TargetAudienceOptionId,
} from 'src/Lca/targetAudience';
import {
  buildReadinessMetrics,
  formatNumber,
  getFlowchartStageSummaries,
  getStageDisplayName,
  getSection,
  getSectionCustomContent,
  getLifecycleBoundaryLabel,
  getMaterialProfile,
  getOrderedStageEntries,
  getReadinessHighlights,
  getSelectedImpacts,
  getWeightedRecycledShare,
  sanitizeRichTextHtml,
  sectionHasDataGap,
} from './utils';
import {
  BuilderPreviewData,
  ReportConfig,
  ReportLanguage,
  ReportProductData,
  ReportSectionImage,
  ReportValidationIssue,
  SectionPreview,
} from './types';
import { LcaDiagramSchema } from '../types';

const PDF_COPY: Record<
  ReportLanguage,
  {
    page: string;
    draft: string;
    notProvided: string;
    dataNotAvailableSelectedScope: string;
    dataNotAvailable: string;
    envirlyWebsite: string;
    methodologyOverviewParagraphs: string[];
    methodologyReferenceLines: string[];
    methodologyLimitationText: string;
    lifeCycleStageFallbackDescription: string;
    closing: {
      eyebrow: string;
      title: string;
      body: string;
      contact: string;
      footer: string;
    };
    onePager: {
      label: string;
      intro: string;
      basicProductInformation: string;
      imageMissing: string;
      environmentalResults: string;
      shortSummary: string;
      studyFramework: string;
      studyRequirementsMissing: string;
    };
  }
> = {
  en: {
    page: 'Page',
    draft: 'DRAFT',
    notProvided: 'Not provided',
    dataNotAvailableSelectedScope: 'Data not available for selected scope.',
    dataNotAvailable: 'Data not available.',
    envirlyWebsite: 'envirly.com',
    methodologyOverviewParagraphs: [
      'Life Cycle Assessment (LCA) is a structured method for evaluating the environmental impacts of a product across its full life cycle, from raw material extraction through manufacturing, transport, use and end-of-life treatment.',
      'The purpose of LCA is not only to calculate a total result, but also to identify which stages and processes contribute most to the overall environmental burden and where the main environmental hotspots are located.',
      'The environmental results in this report should therefore be read both as a total value and as a stage-level profile that supports design, sourcing and improvement decisions.',
    ],
    methodologyReferenceLines: [
      'ISO 14040 provides the overall framework and principles for Life Cycle Assessment.',
      'ISO 14044 defines the requirements and guidelines for goal and scope definition, life cycle inventory analysis, impact assessment, interpretation and reporting.',
      'Where the report is used to communicate climate-related product results, ISO 14067 can be used as an additional reference for carbon footprint quantification and reporting.',
    ],
    methodologyLimitationText:
      'LCA results depend on the goal and scope of the study, system boundaries, source data quality, dataset selection and modelling assumptions. They should therefore be interpreted as decision-support information for hotspot identification and improvement planning, not as context-free standalone values.',
    lifeCycleStageFallbackDescription:
      'Covers the product activities assigned to this life cycle stage in the current model.',
    closing: {
      eyebrow: 'Prepared by Envirly by Quantifier',
      title: 'Report prepared by Envirly by Quantifier',
      body: "Envirly is a technology company that offers an innovative platform for managing the sustainability and carbon footprint of organizations and products. With the platform, entrepreneurs can continuously track and analyze their company's environmental impact, create non-financial reports and adopt decarbonisation measures aimed at green transformation, while generating a positive economic return on investment.",
      contact: 'Contact',
      footer:
        'Contact Envirly to extend this report into ongoing sustainability, carbon footprint and decarbonisation workflows.',
    },
    onePager: {
      label: 'LCA One-Pager',
      intro:
        'One-page LCA summary with core product facts, results and study framework.',
      basicProductInformation: 'Basic product information',
      imageMissing: 'Product image not available',
      environmentalResults: 'Environmental results',
      shortSummary: 'Short summary',
      studyFramework: 'LCA study framework',
      studyRequirementsMissing:
        'Study requirements were not fully provided in the source data.',
    },
  },
  pl: {
    page: 'Strona',
    draft: 'WERSJA ROBOCZA',
    notProvided: 'Nie podano',
    dataNotAvailableSelectedScope: 'Brak danych dla wybranego zakresu.',
    dataNotAvailable: 'Brak danych.',
    envirlyWebsite: 'envirly.com',
    methodologyOverviewParagraphs: [
      'Ocena cyklu życia (LCA) jest usystematyzowaną metodą oceny wpływu środowiskowego produktu w całym jego cyklu życia, od pozyskania surowców przez produkcję, transport i użytkowanie aż po zagospodarowanie po zakończeniu życia.',
      'Celem LCA nie jest wyłącznie obliczenie wyniku łącznego, ale również wskazanie etapów i procesów, które w największym stopniu przyczyniają się do całkowitego obciążenia środowiskowego oraz gdzie znajdują się główne hotspoty środowiskowe.',
      'Wyniki środowiskowe w tym raporcie należy więc czytać zarówno jako wartość całkowitą, jak i profil etapów wspierający decyzje projektowe, zakupowe i doskonalące.',
    ],
    methodologyReferenceLines: [
      'ISO 14040 określa ogólne ramy i zasady oceny cyklu życia.',
      'ISO 14044 definiuje wymagania i wytyczne dotyczące celu i zakresu, analizy zbioru wejść i wyjść, oceny wpływu, interpretacji oraz raportowania.',
      'Gdy raport jest używany do komunikacji klimatycznych wyników produktu, dodatkowym odniesieniem do kwantyfikacji i raportowania śladu węglowego może być ISO 14067.',
    ],
    methodologyLimitationText:
      'Wyniki LCA zależą od celu i zakresu badania, granic systemu, jakości danych źródłowych, doboru baz danych i założeń modelowych. Należy je więc interpretować jako wsparcie decyzji do identyfikacji hotspotów i planowania usprawnień, a nie jako wartości oderwane od kontekstu.',
    lifeCycleStageFallbackDescription:
      'Obejmuje działania produktowe przypisane do tego etapu cyklu życia w aktualnym modelu.',
    closing: {
      eyebrow: 'Przygotowane przez Envirly by Quantifier',
      title: 'Raport przygotowany przez Envirly by Quantifier',
      body: 'Envirly to firma technologiczna oferująca innowacyjną platformę do zarządzania zrównoważonym rozwojem i śladem węglowym organizacji oraz produktów. Dzięki platformie przedsiębiorcy mogą stale monitorować i analizować wpływ środowiskowy swojej firmy, tworzyć raporty niefinansowe i wdrażać działania dekarbonizacyjne wspierające zieloną transformację, jednocześnie generując pozytywny ekonomiczny zwrot z inwestycji.',
      contact: 'Kontakt',
      footer:
        'Skontaktuj się z Envirly, aby rozwinąć ten raport w ciągłe procesy zrównoważonego rozwoju, śladu węglowego i dekarbonizacji.',
    },
    onePager: {
      label: 'LCA One-Pager',
      intro:
        'Jednostronicowe podsumowanie LCA z kluczowymi danymi produktu, wynikami i ramami badania.',
      basicProductInformation: 'Podstawowe informacje o produkcie',
      imageMissing: 'Brak dostępnego zdjęcia produktu',
      environmentalResults: 'Wyniki środowiskowe',
      shortSummary: 'Krótkie podsumowanie',
      studyFramework: 'Ramy badania LCA',
      studyRequirementsMissing:
        'Wymagania badania nie zostały w pełni dostarczone w danych źródłowych.',
    },
  },
  sv: {
    page: 'Sida',
    draft: 'UTKAST',
    notProvided: 'Inte angivet',
    dataNotAvailableSelectedScope:
      'Data är inte tillgänglig för det valda omfånget.',
    dataNotAvailable: 'Data är inte tillgänglig.',
    envirlyWebsite: 'envirly.com',
    methodologyOverviewParagraphs: [
      'Livscykelanalys (LCA) är en strukturerad metod för att bedöma en produkts miljöpåverkan genom hela livscykeln, från råvaruutvinning via tillverkning, transport och användning till hantering vid livets slut.',
      'Syftet med LCA är inte bara att beräkna ett totalresultat utan också att identifiera vilka stadier och processer som bidrar mest till den totala miljöbelastningen och var de viktigaste miljömässiga fokusområdena finns.',
      'Miljöresultaten i denna rapport bör därför läsas både som ett totalvärde och som en etapprofil som stödjer beslut inom design, inköp och förbättringsarbete.',
    ],
    methodologyReferenceLines: [
      'ISO 14040 anger de övergripande ramarna och principerna för livscykelanalys.',
      'ISO 14044 definierar kraven och riktlinjerna för mål och omfattning, inventeringsanalys, miljöpåverkansbedömning, tolkning och rapportering.',
      'När rapporten används för att kommunicera klimatrelaterade produktresultat kan ISO 14067 användas som ett kompletterande stöd för kvantifiering och rapportering av koldioxidavtryck.',
    ],
    methodologyLimitationText:
      'LCA-resultat beror på studiens mål och omfattning, systemgränser, kvaliteten på källdata, val av databaser och modelleringsantaganden. De bör därför tolkas som beslutsstöd för att identifiera fokusområden och planera förbättringar, inte som fristående värden utan sammanhang.',
    lifeCycleStageFallbackDescription:
      'Omfattar de produktaktiviteter som i den aktuella modellen har tilldelats detta livscykelstadium.',
    closing: {
      eyebrow: 'Framtaget av Envirly by Quantifier',
      title: 'Rapport framtagen av Envirly by Quantifier',
      body: 'Envirly är ett teknikföretag som erbjuder en innovativ plattform för att hantera organisationers och produkters hållbarhet och koldioxidavtryck. Med plattformen kan företag kontinuerligt följa och analysera sin miljöpåverkan, skapa icke-finansiella rapporter och genomföra avkarboniseringsåtgärder som stöder den gröna omställningen samtidigt som de skapar en positiv ekonomisk avkastning på investeringen.',
      contact: 'Kontakt',
      footer:
        'Kontakta Envirly för att utveckla denna rapport till löpande arbetsflöden för hållbarhet, koldioxidavtryck och avkarbonisering.',
    },
    onePager: {
      label: 'LCA One-Pager',
      intro:
        'Ensidig LCA-sammanfattning med centrala produktfakta, resultat och studieram.',
      basicProductInformation: 'Grundläggande produktinformation',
      imageMissing: 'Produktbild saknas',
      environmentalResults: 'Miljöresultat',
      shortSummary: 'Kort sammanfattning',
      studyFramework: 'Studieram för LCA',
      studyRequirementsMissing:
        'Studiekraven angavs inte fullständigt i källdatan.',
    },
  },
};

const getPdfCopy = (language: ReportLanguage) =>
  PDF_COPY[language] || PDF_COPY.en;

const FOOTPRINT_UNIT = 'kg CO2e';
const ENVIRLY_CONTACT = {
  website: 'www.envirly.com',
  phone: '+48 698 759 206',
  email: 'contact@envirly.com',
} as const;

const selectPdfValue = <T,>(
  language: ReportLanguage,
  values: Record<ReportLanguage, T>
) => values[language] || values.en;

const tReportBuilder = (
  language: ReportLanguage,
  key: string,
  options?: Record<string, unknown>
) =>
  i18n.t(`lca:reportBuilder.${key}`, { lng: language, ...options }) as string;

const tLca = (
  language: ReportLanguage,
  key: string,
  options?: Record<string, unknown>
) => i18n.t(`lca:${key}`, { lng: language, ...options }) as string;

const getLocalizedHotspotName = (
  stageKey: string | null | undefined,
  fallbackName: string,
  language: ReportLanguage
) => getStageDisplayName(stageKey || '', language) || fallbackName;

const EnvirlyPdfLogo = ({
  width = 180,
  height = 50,
}: {
  width?: number;
  height?: number;
}) => (
  <Svg width={width} height={height} viewBox="0 0 135 37">
    <Path
      d="M9.49442 9.1792C13.2643 9.1792 16.5216 11.4073 18.0543 14.6346C15.6391 20.1769 9.5454 21.5703 4.66853 19.1292C9.49422 18.8007 12.9204 17.7579 14.3425 14.6362C13.1765 13.2438 11.4372 12.3595 9.49442 12.3595C6.36911 12.3595 3.76988 14.6466 3.23795 17.6588C5.40901 14.7316 8.10837 13.7813 11.2458 14.469C6.42794 14.2374 3.88796 17.2537 3.88796 20.3438C3.88796 23.9024 7.44385 25.6819 10.326 25.6819C13.7976 25.6819 17.5468 23.6208 18.9421 19.7524C18.4704 24.6187 14.4198 28.422 9.49422 28.422C4.25165 28.422 0 24.1135 0 18.8007C0 13.4878 4.25185 9.1792 9.49442 9.1792Z"
      fill="#FFFFFF"
      fillRule="evenodd"
    />
    <Path
      d="M42.773 27.8501H38.3626V18.3561C38.3626 16.8577 37.9801 15.6769 37.2151 14.8138C36.4502 13.9387 35.4163 13.5012 34.1135 13.5012C32.7748 13.5012 31.6991 13.9627 30.8864 14.8857C30.652 15.1484 30.4514 15.4375 30.2847 15.7531L30.1557 11.3748C30.5812 10.9713 31.0578 10.6312 31.5856 10.3545C32.6494 9.79107 33.8924 9.50937 35.3147 9.50937C37.6335 9.50937 39.4562 10.2166 40.7829 11.6311C42.1096 13.0457 42.773 14.9996 42.773 17.493V27.8501Z"
      fill="#FFFFFF"
    />
    <Path
      d="M28.2656 9.86899L28.7952 27.8501H25.2568V9.86899H28.2656Z"
      fill="#FFFFFF"
    />
    <Path
      d="M59.3111 27.8501L52.8647 9.86899H48.1666L55.5531 27.8501H59.3111Z"
      fill="#FFFFFF"
    />
    <Path
      d="M60.7186 25.6681L67.2246 9.86899H62.5273L58.7956 20.304L60.7186 25.6681Z"
      fill="#FFFFFF"
    />
    <Path
      d="M75.7557 5.10399C75.1341 5.10399 74.6082 4.88822 74.1779 4.45667C73.7596 4.01314 73.5505 3.4797 73.5505 2.85635C73.5505 2.24499 73.7596 1.72354 74.1779 1.29199C74.6082 0.848457 75.1341 0.626689 75.7557 0.626689C76.3891 0.626689 76.921 0.848457 77.3513 1.29199C77.7816 1.72354 77.9967 2.24499 77.9967 2.85635C77.9967 3.4797 77.7816 4.01314 77.3513 4.45667C76.921 4.88822 76.3891 5.10399 75.7557 5.10399Z"
      fill="#FFFFFF"
    />
    <Path
      d="M73.5505 27.8501V9.86899H77.9609V27.8501H73.5505Z"
      fill="#FFFFFF"
    />
    <Path
      d="M89.9909 27.8501H85.739V9.86899H89.4613L89.9909 27.8501Z"
      fill="#FFFFFF"
    />
    <Path
      d="M91.4654 15.2466C91.8055 14.8345 92.2035 14.4865 92.6594 14.2024C93.4602 13.711 94.3865 13.4652 95.4383 13.4652C96.1315 13.4652 96.8486 13.5731 97.5897 13.7889L98.1275 9.79707C97.757 9.70117 97.3805 9.62924 96.998 9.58129C96.6275 9.53334 96.263 9.50937 95.9044 9.50937C94.5299 9.50937 93.3108 9.82704 92.247 10.4624C91.9259 10.6563 91.6248 10.8743 91.3438 11.1164L91.4654 15.2466Z"
      fill="#FFFFFF"
    />
    <Path d="M108.38 27.8501H103.665V0.15918H108.38V27.8501Z" fill="#FFFFFF" />
    <Path
      d="M123.867 36.8407H119.261L130.386 9.86899H135.001L123.867 36.8407Z"
      fill="#FFFFFF"
    />
    <Path
      d="M118.955 9.86899L123.859 20.3361L121.739 25.4774L113.989 9.86899H118.955Z"
      fill="#FFFFFF"
    />
  </Svg>
);

let fontsRegistered = false;
if (!fontsRegistered) {
  registerFonts();
  fontsRegistered = true;
}

const styles = StyleSheet.create({
  page: {
    paddingTop: 36,
    paddingBottom: 36,
    paddingHorizontal: 36,
    fontFamily: 'Poppins',
    fontSize: 10,
    color: '#2f2f2f',
    lineHeight: 1.35,
    position: 'relative',
  },
  titlePage: {
    fontFamily: 'Poppins',
    fontSize: 10,
    color: '#2f2f2f',
    lineHeight: 1.35,
    position: 'relative',
    backgroundColor: '#FFFFFF',
  },
  titlePageContent: {
    paddingTop: 36,
    paddingBottom: 36,
    paddingHorizontal: 36,
    position: 'relative',
  },
  title: {
    fontSize: 20,
    color: '#165D5C',
    marginBottom: 6,
  },
  subtitle: {
    fontSize: 12,
    color: '#6E6E6E',
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 14,
    color: '#165D5C',
    marginBottom: 10,
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 4,
    borderBottomColor: '#E6E1D7',
    borderBottomWidth: 1,
    paddingBottom: 2,
  },
  label: {
    color: '#2f2f2f',
    width: '58%',
  },
  value: {
    color: '#4d4d4d',
    width: '42%',
    textAlign: 'right',
  },
  pageFooter: {
    position: 'absolute',
    bottom: 22,
    left: 36,
    right: 36,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    color: '#6E6E6E',
    fontSize: 9,
  },
  chip: {
    marginTop: 8,
    backgroundColor: '#F3F0E9',
    color: '#6D3207',
    borderRadius: 8,
    paddingVertical: 6,
    paddingHorizontal: 8,
  },
  warning: {
    marginTop: 8,
    borderColor: '#C4A465',
    borderWidth: 1,
    borderStyle: 'solid',
    borderRadius: 8,
    backgroundColor: '#FAF3E3',
    padding: 8,
    fontSize: 9,
    color: '#6D3207',
  },
  watermark: {
    position: 'absolute',
    top: '45%',
    left: '18%',
    transform: 'rotate(-25deg)',
    fontSize: 48,
    color: '#D9C8A2',
    opacity: 0.2,
    fontFamily: 'Lato',
    fontStyle: 'normal',
  },
  logo: {
    width: 120,
    height: 60,
    objectFit: 'contain',
    marginBottom: 12,
  },
  titleCoverFullPageImage: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  },
  titleCoverImage: {
    width: '100%',
    height: 180,
    objectFit: 'cover',
    borderRadius: 12,
    marginBottom: 18,
  },
  titleContentOverlay: {
    backgroundColor: 'rgba(255, 255, 255, 0.88)',
    borderRadius: 12,
    padding: 18,
  },
  titleCoverAbstract: {
    width: '100%',
    height: 180,
    borderRadius: 12,
    marginBottom: 18,
    padding: 18,
    justifyContent: 'flex-end',
  },
  titleCoverAbstractEyebrow: {
    fontSize: 10,
    color: '#FFFFFF',
    opacity: 0.9,
    marginBottom: 4,
  },
  titleCoverAbstractTitle: {
    fontSize: 24,
    color: '#FFFFFF',
    lineHeight: 1.2,
  },
  neutralBox: {
    borderColor: '#D9D9D9',
    borderWidth: 1,
    borderStyle: 'solid',
    borderRadius: 8,
    backgroundColor: '#F7F7F7',
    padding: 10,
    color: '#6E6E6E',
    marginTop: 8,
  },
  leadText: {
    marginBottom: 10,
    color: '#4d4d4d',
  },
  subheading: {
    marginTop: 12,
    marginBottom: 6,
    color: '#165D5C',
    fontSize: 11,
  },
  listItem: {
    marginBottom: 5,
    color: '#4d4d4d',
  },
  customContentWrap: {
    marginTop: 14,
    paddingTop: 10,
    borderTopColor: '#E6E1D7',
    borderTopWidth: 1,
  },
  richTextParagraph: {
    marginBottom: 8,
    color: '#4d4d4d',
  },
  richTextHeading: {
    marginTop: 4,
    marginBottom: 8,
    color: '#165D5C',
    fontSize: 12,
  },
  richTextStrong: {
    fontFamily: 'Poppins',
    fontWeight: 700,
  },
  richTextEmphasis: {
    fontFamily: 'Poppins',
    fontStyle: 'italic',
  },
  richTextQuote: {
    marginBottom: 8,
    paddingLeft: 10,
    borderLeftColor: '#C4A465',
    borderLeftWidth: 3,
  },
  richTextQuoteText: {
    color: '#6D3207',
  },
  richTextListItem: {
    marginBottom: 5,
    color: '#4d4d4d',
    paddingLeft: 8,
  },
  richTextLink: {
    color: '#165D5C',
    textDecoration: 'underline',
  },
  customImageWrap: {
    marginTop: 8,
    marginBottom: 10,
  },
  customImage: {
    width: '100%',
    objectFit: 'contain',
    borderRadius: 6,
  },
  customImageCaption: {
    marginTop: 4,
    fontSize: 9,
    color: '#6E6E6E',
  },
  sectionCard: {
    marginTop: 10,
    marginBottom: 10,
    padding: 10,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#E6E1D7',
    backgroundColor: '#FBFAF7',
  },
  metricGrid: {
    display: 'flex',
    flexDirection: 'row',
    marginTop: 10,
    marginBottom: 8,
  },
  metricCard: {
    flexGrow: 1,
    padding: 8,
    borderRadius: 8,
    backgroundColor: '#F3F0E9',
  },
  metricLabel: {
    fontSize: 8,
    color: '#6E6E6E',
    marginBottom: 4,
    textTransform: 'uppercase',
  },
  metricValue: {
    fontSize: 13,
    color: '#165D5C',
  },
  metricSubvalue: {
    marginTop: 3,
    fontSize: 8,
    color: '#6E6E6E',
  },
  chartBlock: {
    marginTop: 10,
    marginBottom: 8,
  },
  chartHeader: {
    marginBottom: 8,
    color: '#6E6E6E',
  },
  chartLayout: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  donutWrap: {
    width: 96,
    height: 96,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  donutCaption: {
    marginTop: 6,
    fontSize: 8,
    color: '#6E6E6E',
    textAlign: 'center',
  },
  barsWrap: {
    flexGrow: 1,
  },
  chartRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  chartLabelWrap: {
    width: '30%',
    marginRight: 8,
  },
  chartLabel: {
    fontSize: 9,
    color: '#2F2F2F',
  },
  chartSubLabel: {
    fontSize: 8,
    color: '#6E6E6E',
  },
  chartTrackWrap: {
    width: '42%',
    marginRight: 8,
  },
  chartTrack: {
    width: '100%',
    height: 8,
    backgroundColor: '#E8E4DB',
    borderRadius: 999,
    overflow: 'hidden',
  },
  chartValueWrap: {
    width: '28%',
    alignItems: 'flex-end',
  },
  chartValue: {
    fontSize: 9,
    color: '#2F2F2F',
  },
  chartPercentage: {
    fontSize: 8,
    color: '#6E6E6E',
  },
  hotspotCard: {
    marginTop: 8,
    padding: 8,
    borderRadius: 8,
    backgroundColor: '#F6F8F7',
    borderWidth: 1,
    borderColor: '#DDE7E4',
  },
  flowchartGroupTitle: {
    marginTop: 12,
    marginBottom: 6,
    color: '#165D5C',
    fontSize: 10,
  },
  flowchartGrid: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  flowchartCard: {
    width: '48%',
    padding: 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E6E1D7',
    backgroundColor: '#FFFFFF',
    marginBottom: 8,
  },
  flowchartStageCode: {
    fontSize: 8,
    color: '#6E6E6E',
    textTransform: 'uppercase',
  },
  flowchartStageName: {
    marginTop: 2,
    marginBottom: 4,
    color: '#165D5C',
    fontSize: 11,
  },
  flowchartMeta: {
    fontSize: 8,
    color: '#6E6E6E',
    marginBottom: 2,
  },
  impactCard: {
    marginTop: 10,
    padding: 10,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#E6E1D7',
    backgroundColor: '#FFFFFF',
  },
  closingPage: {
    paddingTop: 40,
    paddingBottom: 40,
    paddingHorizontal: 40,
    backgroundColor: '#165D5C',
    color: '#FFFFFF',
    fontFamily: 'Poppins',
  },
  closingLogo: {
    width: 180,
    height: 50,
    marginBottom: 24,
    justifyContent: 'center',
  },
  closingEyebrow: {
    fontSize: 12,
    letterSpacing: 1.2,
    textTransform: 'uppercase',
    opacity: 0.88,
    marginBottom: 10,
  },
  closingTitle: {
    fontSize: 24,
    lineHeight: 1.3,
    marginBottom: 18,
    color: '#FFFFFF',
  },
  closingText: {
    fontSize: 11,
    lineHeight: 1.6,
    color: '#F4FAF8',
  },
  closingContactCard: {
    marginTop: 26,
    padding: 16,
    borderRadius: 10,
    backgroundColor: 'rgba(255,255,255,0.12)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.18)',
  },
  closingContactTitle: {
    fontSize: 14,
    color: '#FFFFFF',
    marginBottom: 10,
  },
  closingContactItem: {
    fontSize: 12,
    color: '#FFFFFF',
    marginBottom: 6,
  },
  closingFooter: {
    position: 'absolute',
    left: 40,
    right: 40,
    bottom: 32,
    fontSize: 10,
    color: '#DCEEE9',
  },
  onePagerPage: {
    paddingTop: 18,
    paddingBottom: 18,
    paddingHorizontal: 18,
    fontFamily: 'Poppins',
    fontSize: 8.6,
    color: '#24403F',
    backgroundColor: '#F8FBFA',
  },
  onePagerHeader: {
    backgroundColor: '#165D5C',
    borderRadius: 16,
    padding: 16,
    marginBottom: 10,
  },
  onePagerHeaderTop: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
  },
  onePagerProducerLogoBadge: {
    width: 120,
    height: 42,
    borderRadius: 10,
    backgroundColor: '#FFFFFF',
    padding: 6,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  onePagerProducerLogo: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  },
  onePagerEnvirlyLogo: {
    width: 122,
    height: 34,
    objectFit: 'contain',
  },
  onePagerLabel: {
    fontSize: 9,
    color: '#D7F0E8',
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: 4,
  },
  onePagerTitle: {
    fontSize: 19,
    color: '#FFFFFF',
    lineHeight: 1.2,
    marginBottom: 6,
  },
  onePagerIntro: {
    fontSize: 8.2,
    color: '#E8F7F2',
    lineHeight: 1.35,
  },
  onePagerTopRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  onePagerLeftColumn: {
    width: '58%',
  },
  onePagerRightColumn: {
    width: '38%',
  },
  onePagerCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#DCE9E5',
    padding: 10,
  },
  onePagerCardTitle: {
    fontSize: 10,
    color: '#165D5C',
    marginBottom: 6,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
  },
  onePagerBodyText: {
    fontSize: 8,
    color: '#49605F',
    lineHeight: 1.35,
  },
  onePagerInfoRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 4,
    borderBottomWidth: 1,
    borderBottomColor: '#EEF4F2',
    paddingBottom: 3,
  },
  onePagerInfoLabel: {
    width: '42%',
    fontSize: 7.8,
    color: '#6E8281',
  },
  onePagerInfoValue: {
    width: '58%',
    fontSize: 7.8,
    color: '#24403F',
    textAlign: 'right',
  },
  onePagerImageFrame: {
    backgroundColor: '#FFFFFF',
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#DCE9E5',
    padding: 8,
    minHeight: 186,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  onePagerProductImage: {
    width: '100%',
    maxHeight: 162,
    objectFit: 'contain',
  },
  onePagerImageFallback: {
    fontSize: 10,
    color: '#7B8F8D',
    textAlign: 'center',
  },
  onePagerResultsCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#DCE9E5',
    padding: 10,
    marginBottom: 8,
  },
  onePagerResultsGrid: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    marginBottom: 6,
  },
  onePagerResultMetric: {
    width: '48.5%',
    borderRadius: 10,
    padding: 7,
    marginBottom: 6,
    backgroundColor: '#F2F7F5',
  },
  onePagerResultMetricPrimary: {
    backgroundColor: '#165D5C',
  },
  onePagerResultLabel: {
    fontSize: 7.4,
    color: '#6A817F',
    marginBottom: 3,
    textTransform: 'uppercase',
  },
  onePagerResultLabelPrimary: {
    color: '#D7F0E8',
  },
  onePagerResultValue: {
    fontSize: 11.5,
    color: '#165D5C',
    marginBottom: 1,
  },
  onePagerResultValuePrimary: {
    color: '#FFFFFF',
  },
  onePagerResultUnit: {
    fontSize: 7.3,
    color: '#6A817F',
  },
  onePagerResultUnitPrimary: {
    color: '#D7F0E8',
  },
  onePagerStageChartWrap: {
    marginTop: 4,
  },
  onePagerStageChartRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 5,
  },
  onePagerStageCode: {
    width: '10%',
    fontSize: 7.8,
    color: '#165D5C',
  },
  onePagerStageTrackWrap: {
    width: '58%',
    marginRight: 8,
  },
  onePagerStageTrack: {
    width: '100%',
    height: 5,
    borderRadius: 999,
    backgroundColor: '#E5EFEC',
    overflow: 'hidden',
  },
  onePagerStageValue: {
    width: '32%',
    fontSize: 7.4,
    color: '#486261',
    textAlign: 'right',
  },
  onePagerBottomRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  onePagerSummaryCard: {
    width: '60%',
    backgroundColor: '#FFFFFF',
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#DCE9E5',
    padding: 10,
  },
  onePagerRequirementsCard: {
    width: '36%',
    backgroundColor: '#FFFFFF',
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#DCE9E5',
    padding: 10,
  },
  onePagerBullet: {
    fontSize: 7.8,
    color: '#486261',
    marginBottom: 4,
  },
  onePagerFooterLine: {
    marginTop: 6,
    fontSize: 7.1,
    color: '#6C8583',
  },
});

const getTextValue = (
  product: ReportProductData | null,
  key: keyof ReportProductData
): string | null => {
  const value = product?.[key];
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
};

const normalizeHexColor = (hex: string | undefined) => {
  const sanitized = (hex || '').trim();
  if (!/^#([0-9a-fA-F]{6})$/.test(sanitized)) {
    return DEFAULT_REPORT_ACCENT_COLOR;
  }
  return sanitized.toUpperCase();
};

const mixHexColors = (baseHex: string, targetHex: string, weight: number) => {
  const normalizedWeight = Math.min(Math.max(weight, 0), 1);
  const base = normalizeHexColor(baseHex);
  const target = normalizeHexColor(targetHex);
  const mixChannel = (start: number, end: number) =>
    Math.round(start + (end - start) * normalizedWeight);
  const baseRgb = [
    Number.parseInt(base.slice(1, 3), 16),
    Number.parseInt(base.slice(3, 5), 16),
    Number.parseInt(base.slice(5, 7), 16),
  ];
  const targetRgb = [
    Number.parseInt(target.slice(1, 3), 16),
    Number.parseInt(target.slice(3, 5), 16),
    Number.parseInt(target.slice(5, 7), 16),
  ];

  return `#${baseRgb
    .map((channel, index) =>
      mixChannel(channel, targetRgb[index]).toString(16).padStart(2, '0')
    )
    .join('')}`.toUpperCase();
};

const getReportAccentTheme = (config: ReportConfig) => {
  const primary = normalizeHexColor(config.branding.accentColor);
  const intensityMap: Record<
    ReportConfig['branding']['accentIntensity'],
    number
  > = {
    low: 0.84,
    medium: 0.76,
    high: 0.68,
  };
  const surfaceWeight = intensityMap[config.branding.accentIntensity];

  return {
    primary,
    heading: mixHexColors(primary, '#000000', 0.18),
    surface: mixHexColors(primary, '#FFFFFF', surfaceWeight),
    soft: mixHexColors(primary, '#FFFFFF', Math.min(surfaceWeight + 0.1, 0.92)),
    border: mixHexColors(primary, '#FFFFFF', 0.58),
    inverseText: '#FFFFFF',
    muted: mixHexColors(primary, '#6E6E6E', 0.72),
  };
};

type PdfProps = {
  config: ReportConfig;
  previewData: BuilderPreviewData;
  diagramData: LcaDiagramSchema | null;
  sections: SectionPreview[];
  issues: ReportValidationIssue[];
  product: ReportProductData | null;
  isDraft: boolean;
};

const renderFooter = (config: ReportConfig, pageNumber: number) => {
  const copy = getPdfCopy(config.language);
  return (
    <View fixed style={styles.pageFooter}>
      <Text>
        {config.layout.footerOptions.showWebsite ? copy.envirlyWebsite : ''}
      </Text>
      <Text>
        {config.layout.footerOptions.showPageNumbers
          ? `${copy.page} ${pageNumber}`
          : ''}
      </Text>
    </View>
  );
};

const getPdfImageWidth = (layout: ReportSectionImage['layout']) => {
  if (layout === 'full') return '100%';
  if (layout === 'medium') return '65%';
  return '36%';
};

const toAbsoluteAssetUrl = (url?: string | null) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  if (typeof window === 'undefined') return url;
  return `${window.location.origin}${url}`;
};

const truncateText = (value: string | null | undefined, maxLength: number) => {
  if (!value) return null;
  const normalized = value.replace(/\s+/g, ' ').trim();
  if (normalized.length <= maxLength) return normalized;
  return `${normalized.slice(0, Math.max(0, maxLength - 1)).trim()}…`;
};

const STAGE_CHART_COLORS: Record<string, string> = {
  'raw-materials-extraction-and-processing': '#165D5C',
  'transport-of-raw-materials': '#3F8C88',
  'production-and-packaging': '#6FAE9B',
  'distribution-and-storage': '#CC9A56',
  installation: '#E3B35A',
  'use-stage': '#7E8C8D',
  'end-of-life': '#AD6A6C',
};

const getEcoinventReference = (
  language: ReportLanguage,
  ecoinventVersion: string | null | undefined
) =>
  selectPdfValue(language, {
    en: `Background modelling may use datasets from the ecoinvent database${
      ecoinventVersion ? ` (version ${ecoinventVersion})` : ''
    }, one of the most widely used life cycle inventory databases in LCA practice. It provides verified datasets for materials, energy, transport, waste treatment and other background processes where primary product-specific data is not available.`,
    pl: `Modelowanie tła może wykorzystywać zbiory danych z bazy ecoinvent${
      ecoinventVersion ? ` (wersja ${ecoinventVersion})` : ''
    }, jednej z najczęściej używanych baz danych inwentarzowych LCA. Dostarcza ona zweryfikowanych danych dla materiałów, energii, transportu, zagospodarowania odpadów i innych procesów tła tam, gdzie nie są dostępne pierwotne dane produktowe.`,
    sv: `Bakgrundsmodelleringen kan använda dataset från ecoinvent-databasen${
      ecoinventVersion ? ` (version ${ecoinventVersion})` : ''
    }, en av de mest använda livscykelinventeringsdatabaserna i LCA-praktik. Den tillhandahåller verifierade dataset för material, energi, transport, avfallshantering och andra bakgrundsprocesser där primära produktspecifika data inte finns tillgängliga.`,
  });

const getLifeCycleStageDescription = (
  language: ReportLanguage,
  stageKey: string
) => {
  const descriptions: Record<ReportLanguage, Record<string, string>> = {
    en: {
      'raw-materials-extraction-and-processing':
        'Covers the extraction, cultivation, sourcing and initial processing of raw materials and input materials required to produce the product.',
      'transport-of-raw-materials':
        'Covers the transport of raw materials, components and intermediate goods to the manufacturing site or next processing step.',
      'production-and-packaging':
        'Covers manufacturing, processing, assembly, on-site energy and resource use, and product packaging.',
      'distribution-and-storage':
        'Covers the transport and distribution of the finished product to warehouse, retailer, project site or end user.',
      installation:
        'Covers activities related to installation, assembly or implementation of the product, including installation losses and auxiliary materials where relevant.',
      'use-stage':
        'Covers environmental impacts arising during the use phase, such as energy consumption, maintenance, repair, replacement or other use-related effects during the product lifetime.',
      'end-of-life':
        'Covers deconstruction, collection, transport after use, waste processing, recycling, recovery and final disposal once the product reaches the end of its life.',
    },
    pl: {
      'raw-materials-extraction-and-processing':
        'Obejmuje wydobycie, uprawę, pozyskanie i wstępne przetwarzanie surowców oraz materiałów wejściowych potrzebnych do wytworzenia produktu.',
      'transport-of-raw-materials':
        'Obejmuje transport surowców, komponentów i półproduktów do zakładu produkcyjnego lub kolejnego etapu przetwarzania.',
      'production-and-packaging':
        'Obejmuje wytwarzanie, przetwarzanie, montaż, zużycie energii i zasobów na miejscu oraz pakowanie produktu.',
      'distribution-and-storage':
        'Obejmuje transport i dystrybucję gotowego produktu do magazynu, sprzedawcy, miejsca realizacji lub użytkownika końcowego.',
      installation:
        'Obejmuje działania związane z instalacją, montażem lub wdrożeniem produktu, w tym straty instalacyjne i materiały pomocnicze tam, gdzie mają znaczenie.',
      'use-stage':
        'Obejmuje wpływy środowiskowe powstające w fazie użytkowania, takie jak zużycie energii, utrzymanie, naprawy, wymiany lub inne efekty związane z użytkowaniem w okresie życia produktu.',
      'end-of-life':
        'Obejmuje demontaż, zbiórkę, transport po użyciu, przetwarzanie odpadów, recykling, odzysk i ostateczne unieszkodliwienie po zakończeniu życia produktu.',
    },
    sv: {
      'raw-materials-extraction-and-processing':
        'Omfattar utvinning, odling, anskaffning och inledande bearbetning av råvaror och insatsmaterial som krävs för att tillverka produkten.',
      'transport-of-raw-materials':
        'Omfattar transport av råvaror, komponenter och mellanprodukter till tillverkningsplatsen eller nästa bearbetningssteg.',
      'production-and-packaging':
        'Omfattar tillverkning, bearbetning, montering, platsbunden energi- och resursanvändning samt produktförpackning.',
      'distribution-and-storage':
        'Omfattar transport och distribution av den färdiga produkten till lager, återförsäljare, projektplats eller slutanvändare.',
      installation:
        'Omfattar aktiviteter relaterade till installation, montering eller införande av produkten, inklusive installationsförluster och hjälpmaterial där det är relevant.',
      'use-stage':
        'Omfattar miljöpåverkan under användningsfasen, till exempel energianvändning, underhåll, reparation, utbyte eller andra användningsrelaterade effekter under produktens livslängd.',
      'end-of-life':
        'Omfattar demontering, insamling, transport efter användning, avfallsbehandling, återvinning, återtag och slutligt omhändertagande när produkten når slutet av sin livslängd.',
    },
  };

  return (
    descriptions[language]?.[stageKey] ||
    getPdfCopy(language).lifeCycleStageFallbackDescription
  );
};

const buildAdditionalEnvironmentalRows = (
  product: ReportProductData | null,
  language: ReportLanguage
) =>
  [
    {
      label: tLca(
        language,
        'productPassport.lifeCycleMetrics.productCarbonFootprintGhgEmissions'
      ),
      value: getTextValue(product, 'product_carbon_footprint'),
    },
    {
      label: tLca(
        language,
        'productPassport.lifeCycleMetrics.emissionsAirWaterSoilNoise'
      ),
      value: getTextValue(product, 'emissions_to_air_water_soil_noise'),
    },
    {
      label: tLca(
        language,
        'productPassport.lifeCycleMetrics.releaseMicroplasticsNanoplastics'
      ),
      value: getTextValue(product, 'microplastics_nanoplastics_release'),
    },
    {
      label: tLca(
        language,
        'productPassport.lifeCycleMetrics.consumptionEnergyWaterResources'
      ),
      value: getTextValue(product, 'energy_water_resource_consumption'),
    },
    {
      label: tLca(
        language,
        'productPassport.lifeCycleMetrics.primaryEnergyUse'
      ),
      value: getTextValue(product, 'primary_energy_use'),
    },
    {
      label: tLca(
        language,
        'productPassport.lifeCycleMetrics.recoveredExportedEnergy'
      ),
      value: getTextValue(product, 'recovered_exported_energy'),
    },
    {
      label: tLca(
        language,
        'productPassport.lifeCycleMetrics.productPerformanceUpdatesImpact'
      ),
      value: getTextValue(product, 'product_performance_factors_impact'),
    },
    {
      label: tLca(
        language,
        'productPassport.lifeCycleMetrics.deforestationImpact'
      ),
      value: getTextValue(product, 'deforestation_impact'),
    },
    {
      label: tLca(
        language,
        'productPassport.lifeCycleMetrics.shareRecycledMaterials'
      ),
      value: getTextValue(product, 'recycled_materials_share'),
    },
    {
      label: tLca(
        language,
        'productPassport.lifeCycleMetrics.materialRecovery'
      ),
      value: getTextValue(product, 'material_recovery_critical_raw_materials'),
    },
    {
      label: tLca(
        language,
        'productPassport.lifeCycleMetrics.renewableMaterials'
      ),
      value: getTextValue(product, 'renewable_materials_content'),
    },
    {
      label: tLca(language, 'productPassport.lifeCycleMetrics.wasteGenerated'),
      value: getTextValue(product, 'waste_generated'),
    },
    {
      label: tLca(
        language,
        'productPassport.lifeCycleMetrics.co2SequestrationPotential'
      ),
      value: getTextValue(product, 'co2_sequestration_potential'),
    },
    {
      label: tLca(
        language,
        'productPassport.lifeCycleMetrics.ozoneDepletionPotential'
      ),
      value: getTextValue(product, 'ozone_depletion_potential'),
    },
    {
      label: tLca(
        language,
        'productPassport.lifeCycleMetrics.acidificationPotential'
      ),
      value: getTextValue(product, 'acidification_potential'),
    },
    {
      label: tLca(
        language,
        'productPassport.lifeCycleMetrics.eutrophicationPotential'
      ),
      value: getTextValue(product, 'eutrophication_potential'),
    },
    {
      label: tLca(
        language,
        'productPassport.lifeCycleMetrics.photochemicalOzoneCreationPotential'
      ),
      value: getTextValue(product, 'photochemical_ozone_creation_potential'),
    },
  ].filter((row): row is { label: string; value: string } =>
    Boolean(row.value)
  );

const getLifecycleStageMethodologyRows = (
  flowchartStages: ReturnType<typeof getFlowchartStageSummaries>,
  language: ReportLanguage
) =>
  flowchartStages.map((stage) => ({
    key: stage.key,
    code: stage.code,
    name: stage.name,
    description: getLifeCycleStageDescription(language, stage.key),
  }));

const buildOnePagerSummary = ({
  product,
  previewData,
  visibleImpactsCount,
  language,
}: {
  product: ReportProductData | null;
  previewData: BuilderPreviewData;
  visibleImpactsCount: number;
  language: ReportLanguage;
}) => {
  const summary: string[] = [];
  const primaryHotspot = previewData.hotspots[0];
  const primaryHotspotName = primaryHotspot
    ? getLocalizedHotspotName(
        primaryHotspot.stageKey,
        primaryHotspot.name,
        language
      )
    : null;

  summary.push(
    selectPdfValue(language, {
      en: `${
        product?.name || 'This product'
      } is presented in a ${getLifecycleBoundaryLabel(
        product?.lifecycle_boundaries,
        language
      ).toLowerCase()} study scope.`,
      pl: `${
        product?.name || 'Ten produkt'
      } przedstawiono w zakresie badania ${getLifecycleBoundaryLabel(
        product?.lifecycle_boundaries,
        language
      ).toLowerCase()}.`,
      sv: `${
        product?.name || 'Denna produkt'
      } presenteras inom en ${getLifecycleBoundaryLabel(
        product?.lifecycle_boundaries,
        language
      ).toLowerCase()} studieomfattning.`,
    })
  );

  if (primaryHotspot) {
    summary.push(
      selectPdfValue(language, {
        en: `Main hotspot: ${primaryHotspot.stageCode} (${primaryHotspotName}) with ${primaryHotspot.percentage.toFixed(
          1
        )}% of the primary impact.`,
        pl: `Główny hotspot: ${primaryHotspot.stageCode} (${primaryHotspotName}) odpowiada za ${primaryHotspot.percentage.toFixed(
          1
        )}% głównego wpływu.`,
        sv: `Viktigaste fokusområde: ${primaryHotspot.stageCode} (${primaryHotspotName}) står för ${primaryHotspot.percentage.toFixed(
          1
        )}% av den huvudsakliga påverkan.`,
      })
    );
  }

  let polishIndicatorSuffix = '';
  if (visibleImpactsCount !== 1) {
    polishIndicatorSuffix = visibleImpactsCount < 5 ? 'i' : 'ów';
  }
  const polishEnvironmentalSuffix = visibleImpactsCount === 1 ? 'y' : 'ych';

  summary.push(
    selectPdfValue(language, {
      en: `${visibleImpactsCount} environmental indicator${
        visibleImpactsCount === 1 ? '' : 's'
      } are shown for quick product-level communication.`,
      pl: `Pokazano ${visibleImpactsCount} wskaźnik${polishIndicatorSuffix} środowiskow${polishEnvironmentalSuffix} do szybkiej komunikacji na poziomie produktu.`,
      sv: `${visibleImpactsCount} miljöindikator${
        visibleImpactsCount === 1 ? '' : 'er'
      } visas för snabb kommunikation på produktnivå.`,
    })
  );

  return summary.slice(0, 3);
};

type StageChartRow = {
  key: string;
  code: string;
  name: string;
  value: number;
};

const renderStageDistributionChart = ({
  config,
  rows,
  total,
  title,
  unit,
}: {
  config: ReportConfig;
  rows: StageChartRow[];
  total: number;
  title: string;
  unit?: string;
}) => {
  if (!rows.length || !total) return null;

  const normalizedRows = rows.map((row) => ({
    ...row,
    share: total ? (row.value / total) * 100 : 0,
    color: STAGE_CHART_COLORS[row.key] || '#165D5C',
  }));
  const stripWidth = 92;
  let stripX = 0;

  return (
    <View style={styles.chartBlock}>
      <Text style={styles.chartHeader}>{title}</Text>
      <View style={styles.chartLayout}>
        {config.layout.chartStyle === 'donut-bars' && (
          <View style={styles.donutWrap}>
            <Svg width={stripWidth} height={52}>
              <Rect
                x={0}
                y={18}
                width={stripWidth}
                height={12}
                rx={6}
                ry={6}
                fill="#E8E4DB"
              />
              {normalizedRows.map((row) => {
                const segmentWidth = (row.share / 100) * stripWidth;
                const segment = (
                  <Rect
                    key={row.key}
                    x={stripX}
                    y={18}
                    width={segmentWidth}
                    height={12}
                    fill={row.color}
                  />
                );
                stripX += segmentWidth;
                return segment;
              })}
            </Svg>
            <Text style={styles.donutCaption}>
              {selectPdfValue(config.language, {
                en: `${rows.length} stages with measured contribution`,
                pl: `${rows.length} etapów ze zmierzonym udziałem`,
                sv: `${rows.length} stadier med uppmätt andel`,
              })}
            </Text>
          </View>
        )}

        <View style={styles.barsWrap}>
          {normalizedRows.map((row) => (
            <View key={row.key} style={styles.chartRow}>
              <View style={styles.chartLabelWrap}>
                <Text style={styles.chartLabel}>
                  {row.code} ({row.name})
                </Text>
                <Text style={styles.chartSubLabel}>
                  {selectPdfValue(config.language, {
                    en: 'Share of selected impact',
                    pl: 'Udział wybranego wpływu',
                    sv: 'Andel av vald påverkan',
                  })}
                </Text>
              </View>
              <View style={styles.chartTrackWrap}>
                <View style={styles.chartTrack}>
                  <View
                    style={{
                      width: `${Math.max(row.share, 1.5)}%`,
                      height: '100%',
                      borderRadius: 999,
                      backgroundColor: row.color,
                    }}
                  />
                </View>
              </View>
              <View style={styles.chartValueWrap}>
                <Text style={styles.chartValue}>
                  {formatNumber(
                    row.value,
                    config.layout.numberFormat,
                    config.language
                  )}
                  {config.layout.numberFormat.showUnits && unit
                    ? ` ${unit}`
                    : ''}
                </Text>
                <Text style={styles.chartPercentage}>
                  {row.share.toFixed(1)}%
                </Text>
              </View>
            </View>
          ))}
        </View>
      </View>
    </View>
  );
};

const renderOnePagerStageChart = ({
  config,
  rows,
  unit,
}: {
  config: ReportConfig;
  rows: StageChartRow[];
  unit?: string;
}) => {
  if (!rows.length) return null;

  const maxValue = Math.max(...rows.map((row) => row.value), 0);
  if (!maxValue) return null;

  return (
    <View style={styles.onePagerStageChartWrap}>
      {rows.map((row) => (
        <View key={row.key} style={styles.onePagerStageChartRow}>
          <Text style={styles.onePagerStageCode}>{row.code}</Text>
          <View style={styles.onePagerStageTrackWrap}>
            <View style={styles.onePagerStageTrack}>
              <View
                style={{
                  width: `${Math.max((row.value / maxValue) * 100, 2)}%`,
                  height: '100%',
                  backgroundColor: STAGE_CHART_COLORS[row.key] || '#165D5C',
                  borderRadius: 999,
                }}
              />
            </View>
          </View>
          <Text style={styles.onePagerStageValue}>
            {formatNumber(
              row.value,
              config.layout.numberFormat,
              config.language
            )}
            {config.layout.numberFormat.showUnits && unit ? ` ${unit}` : ''}
          </Text>
        </View>
      ))}
    </View>
  );
};

const renderInlineNodes = (
  nodes: Node[],
  keyPrefix: string
): React.ReactNode[] =>
  nodes.flatMap((node, index) => {
    const key = `${keyPrefix}-${index}`;

    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent || '';
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      return [];
    }

    const element = node as HTMLElement;
    const tagName = element.tagName.toLowerCase();

    if (tagName === 'br') {
      return '\n';
    }

    const children = renderInlineNodes(
      Array.from(element.childNodes),
      `${key}-child`
    );

    if (tagName === 'strong' || tagName === 'b') {
      return (
        <Text key={key} style={styles.richTextStrong}>
          {children}
        </Text>
      );
    }

    if (tagName === 'em' || tagName === 'i') {
      return (
        <Text key={key} style={styles.richTextEmphasis}>
          {children}
        </Text>
      );
    }

    if (tagName === 'a') {
      const href = element.getAttribute('href') || '';

      return (
        <Link key={key} src={href} style={styles.richTextLink}>
          {children}
        </Link>
      );
    }

    return children;
  });

const renderRichTextPdf = (html: string): React.ReactNode => {
  if (!html || typeof DOMParser === 'undefined') {
    return null;
  }

  const parsed = new DOMParser().parseFromString(
    sanitizeRichTextHtml(html),
    'text/html'
  );

  return parsed.body.childNodes.length
    ? Array.from(parsed.body.childNodes).map((node, index) => {
        const key = `rich-text-${index}`;

        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.textContent?.trim();
          if (!text) return null;

          return (
            <Text key={key} style={styles.richTextParagraph}>
              {text}
            </Text>
          );
        }

        if (node.nodeType !== Node.ELEMENT_NODE) {
          return null;
        }

        const element = node as HTMLElement;
        const tagName = element.tagName.toLowerCase();

        if (tagName === 'h2') {
          return (
            <Text key={key} style={styles.richTextHeading}>
              {renderInlineNodes(Array.from(element.childNodes), key)}
            </Text>
          );
        }

        if (tagName === 'blockquote') {
          return (
            <View key={key} style={styles.richTextQuote}>
              <Text style={styles.richTextQuoteText}>
                {renderInlineNodes(Array.from(element.childNodes), key)}
              </Text>
            </View>
          );
        }

        if (tagName === 'ul' || tagName === 'ol') {
          return Array.from(element.children)
            .filter((child) => child.tagName.toLowerCase() === 'li')
            .map((child, childIndex) => (
              <Text
                key={`${key}-li-${childIndex}`}
                style={styles.richTextListItem}
              >
                {tagName === 'ol' ? `${childIndex + 1}. ` : '• '}
                {renderInlineNodes(
                  Array.from(child.childNodes),
                  `${key}-li-${childIndex}`
                )}
              </Text>
            ));
        }

        return (
          <Text key={key} style={styles.richTextParagraph}>
            {renderInlineNodes(Array.from(element.childNodes), key)}
          </Text>
        );
      })
    : null;
};

const SectionPage = ({
  config,
  section,
  previewData,
  diagramData,
  product,
  issues,
  isDraft,
  pageNumber,
}: {
  config: ReportConfig;
  section: SectionPreview;
  previewData: BuilderPreviewData;
  diagramData: LcaDiagramSchema | null;
  product: ReportProductData | null;
  issues: ReportValidationIssue[];
  isDraft: boolean;
  pageNumber: number;
}) => {
  const copy = getPdfCopy(config.language);
  const accentTheme = getReportAccentTheme(config);
  const sectionConfig = getSection(config, section.id);
  const customContent = getSectionCustomContent(sectionConfig);
  const stageRows = getOrderedStageEntries(
    previewData.selectedImpact?.emission_per_stage,
    config.language
  );
  const materialRows = product ? getMaterialProfile(product) : [];
  const readinessMetrics = product
    ? buildReadinessMetrics(product, previewData, config.language)
    : [];
  const readinessHighlights = getReadinessHighlights(
    readinessMetrics,
    config.language
  );
  const additionalEnvironmentalRows = buildAdditionalEnvironmentalRows(
    product,
    config.language
  );
  const selectedImpactCategories = getSelectedImpacts(
    section.id === 'impact-categories' ? sectionConfig : undefined,
    previewData.availableImpacts
  );
  const flowchartStages = getFlowchartStageSummaries(
    diagramData,
    previewData.selectedImpact,
    config.language
  );
  const stageMethodologyRows = getLifecycleStageMethodologyRows(
    flowchartStages,
    config.language
  );
  const getFlowchartGroupTitle = (groupKey: 'cradle' | 'grave') => {
    if (groupKey === 'cradle') {
      return selectPdfValue(config.language, {
        en: 'CRADLE',
        pl: 'POCZĄTEK',
        sv: 'BÖRJAN',
      });
    }

    return selectPdfValue(config.language, {
      en: 'GRAVE',
      pl: 'KONIEC',
      sv: 'SLUTET',
    });
  };
  const ecoinventVersion =
    product?.ecoinvent_version ||
    config.audit.datasetVersions.ecoinvent ||
    null;

  const renderSectionContent = () => {
    if (section.id === 'executive-summary') {
      if (previewData.totalFootprint === null) {
        return (
          <Text style={styles.neutralBox}>
            {copy.dataNotAvailableSelectedScope}
          </Text>
        );
      }

      return (
        <>
          <Text style={styles.leadText}>
            {selectPdfValue(config.language, {
              en: 'This report combines quantified LCA results with structured product data captured in the platform to create a more decision-ready narrative.',
              pl: 'Raport łączy ilościowe wyniki LCA z uporządkowanymi danymi produktowymi zebranymi na platformie, tworząc narrację lepiej wspierającą decyzje.',
              sv: 'Rapporten kombinerar kvantifierade LCA-resultat med strukturerad produktdata från plattformen för att skapa en mer beslutsanpassad berättelse.',
            })}
          </Text>
          <View style={styles.metricGrid}>
            <View style={{ ...styles.metricCard, marginRight: 8 }}>
              <Text style={styles.metricLabel}>
                {selectPdfValue(config.language, {
                  en: 'Total footprint',
                  pl: 'Łączny ślad',
                  sv: 'Totalt avtryck',
                })}
              </Text>
              <Text
                style={{ ...styles.metricValue, color: accentTheme.heading }}
              >
                {formatNumber(
                  previewData.totalFootprint,
                  config.layout.numberFormat,
                  config.language
                )}{' '}
                {FOOTPRINT_UNIT}
              </Text>
              <Text style={styles.metricSubvalue}>
                {selectPdfValue(config.language, {
                  en: 'Selected impact area: ',
                  pl: 'Wybrany obszar wpływu: ',
                  sv: 'Valt påverkansområde: ',
                })}
                {previewData.selectedImpact?.area.name}
              </Text>
            </View>
            <View style={{ ...styles.metricCard, marginRight: 8 }}>
              <Text style={styles.metricLabel}>
                {selectPdfValue(config.language, {
                  en: 'Lifecycle boundary',
                  pl: 'Granica cyklu życia',
                  sv: 'Livscykelgräns',
                })}
              </Text>
              <Text
                style={{ ...styles.metricValue, color: accentTheme.heading }}
              >
                {getLifecycleBoundaryLabel(
                  product?.lifecycle_boundaries,
                  config.language
                )}
              </Text>
              <Text style={styles.metricSubvalue}>
                {selectPdfValue(config.language, {
                  en: `${previewData.availableImpacts.length} impact categories available`,
                  pl: `${previewData.availableImpacts.length} dostępnych kategorii wpływu`,
                  sv: `${previewData.availableImpacts.length} tillgängliga påverkanskategorier`,
                })}
              </Text>
            </View>
            <View style={styles.metricCard}>
              <Text style={styles.metricLabel}>
                {selectPdfValue(config.language, {
                  en: 'Platform coverage',
                  pl: 'Zakres danych platformy',
                  sv: 'Plattformstäckning',
                })}
              </Text>
              <Text
                style={{ ...styles.metricValue, color: accentTheme.heading }}
              >
                {selectPdfValue(config.language, {
                  en: `${product?.materials.length || 0} materials`,
                  pl: `${product?.materials.length || 0} materiałów`,
                  sv: `${product?.materials.length || 0} material`,
                })}
              </Text>
              <Text style={styles.metricSubvalue}>
                {selectPdfValue(config.language, {
                  en: `${flowchartStages.length} flowchart stages configured`,
                  pl: `${flowchartStages.length} skonfigurowanych etapów flowchartu`,
                  sv: `${flowchartStages.length} konfigurerade flödesschemastadier`,
                })}
              </Text>
            </View>
          </View>
          {renderStageDistributionChart({
            config,
            rows: stageRows,
            total: previewData.totalFootprint,
            title: selectPdfValue(config.language, {
              en: 'Stage split for the selected impact area',
              pl: 'Podział na etapy dla wybranego obszaru wpływu',
              sv: 'Etappfördelning för det valda påverkansområdet',
            }),
            unit: previewData.selectedImpact?.area.unit_name,
          })}
          {previewData.hotspots.map((hotspot) => (
            <View style={styles.row} key={hotspot.stageCode}>
              <Text style={styles.label}>
                {config.language === 'pl' ? 'Hotspot ' : 'Hotspot '}
                {hotspot.stageCode} (
                {getLocalizedHotspotName(
                  hotspot.stageKey,
                  hotspot.name,
                  config.language
                )}
                )
              </Text>
              <Text style={styles.value}>{hotspot.percentage.toFixed(1)}%</Text>
            </View>
          ))}
          {readinessMetrics.slice(0, 3).map((metric) => (
            <View style={styles.row} key={metric.label}>
              <Text style={styles.label}>{metric.label}</Text>
              <Text style={styles.value}>
                {Math.round(metric.percentage)}%
                {selectPdfValue(config.language, {
                  en: ' complete',
                  pl: ' kompletności',
                  sv: ' fullständighet',
                })}
              </Text>
            </View>
          ))}
        </>
      );
    }

    if (section.id === 'producer-info') {
      return (
        <>
          <View style={styles.row}>
            <Text style={styles.label}>
              {selectPdfValue(config.language, {
                en: 'Organization',
                pl: 'Organizacja',
                sv: 'Organisation',
              })}
            </Text>
            <Text style={styles.value}>
              {config.branding.companyNameOverride || copy.notProvided}
            </Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>
              {selectPdfValue(config.language, {
                en: 'Manufacturer',
                pl: 'Producent',
                sv: 'Tillverkare',
              })}
            </Text>
            <Text style={styles.value}>
              {product?.manufacturer_name || copy.notProvided}
            </Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>
              {selectPdfValue(config.language, {
                en: 'Importer',
                pl: 'Importer',
                sv: 'Importör',
              })}
            </Text>
            <Text style={styles.value}>
              {product?.importer_name || copy.notProvided}
            </Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>
              {selectPdfValue(config.language, {
                en: 'Production country',
                pl: 'Kraj produkcji',
                sv: 'Produktionsland',
              })}
            </Text>
            <Text style={styles.value}>
              {product?.production_country?.name || copy.notProvided}
            </Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>
              {selectPdfValue(config.language, {
                en: 'Production location',
                pl: 'Lokalizacja produkcji',
                sv: 'Produktionsplats',
              })}
            </Text>
            <Text style={styles.value}>
              {product?.production_location || copy.notProvided}
            </Text>
          </View>
          {product?.key_company_activities && (
            <>
              <Text
                style={{ ...styles.subheading, color: accentTheme.heading }}
              >
                {selectPdfValue(config.language, {
                  en: 'Company activities context',
                  pl: 'Kontekst działalności firmy',
                  sv: 'Sammanhang för företagets verksamhet',
                })}
              </Text>
              <Text>{product.key_company_activities}</Text>
            </>
          )}
        </>
      );
    }

    if (section.id === 'product-description') {
      const weight = product?.weight as number | undefined;
      const dimensions = product?.dimensions as string | undefined;
      const functionalUnit = product?.functional_unit as string | undefined;

      return (
        <>
          {product?.description && (
            <Text style={styles.leadText}>{product.description}</Text>
          )}
          <View style={styles.row}>
            <Text style={styles.label}>
              {selectPdfValue(config.language, {
                en: 'Functional unit',
                pl: 'Jednostka funkcjonalna',
                sv: 'Funktionell enhet',
              })}
            </Text>
            <Text style={styles.value}>
              {functionalUnit || copy.notProvided}
            </Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>
              {selectPdfValue(config.language, {
                en: 'Weight',
                pl: 'Waga',
                sv: 'Vikt',
              })}
            </Text>
            <Text style={styles.value}>
              {weight
                ? `${formatNumber(
                    weight,
                    config.layout.numberFormat,
                    config.language
                  )} ${String(product?.weight_unit || '')}`
                : copy.notProvided}
            </Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>
              {selectPdfValue(config.language, {
                en: 'Dimensions',
                pl: 'Wymiary',
                sv: 'Mått',
              })}
            </Text>
            <Text style={styles.value}>{dimensions || copy.notProvided}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>
              {selectPdfValue(config.language, {
                en: 'GTIN / identification',
                pl: 'GTIN / identyfikacja',
                sv: 'GTIN / identifiering',
              })}
            </Text>
            <Text style={styles.value}>
              {product?.gtin_code ||
                product?.model_batch_serial_number ||
                copy.notProvided}
            </Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>
              {selectPdfValue(config.language, {
                en: 'Data collection period',
                pl: 'Okres zbierania danych',
                sv: 'Datainsamlingsperiod',
              })}
            </Text>
            <Text style={styles.value}>
              {product?.data_collection_period || copy.notProvided}
            </Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>
              {selectPdfValue(config.language, {
                en: 'Revision',
                pl: 'Rewizja',
                sv: 'Revidering',
              })}
            </Text>
            <Text style={styles.value}>
              {product?.revision || copy.notProvided}
            </Text>
          </View>
        </>
      );
    }

    if (section.id === 'methodology-standards') {
      return (
        <>
          <Text style={styles.leadText}>
            {selectPdfValue(config.language, {
              en: 'Methodology summary',
              pl: 'Podsumowanie metodologii',
              sv: 'Metodiköversikt',
            })}
          </Text>
          {copy.methodologyOverviewParagraphs.map((paragraph) => (
            <Text key={paragraph} style={styles.richTextParagraph}>
              {paragraph}
            </Text>
          ))}
          <Text style={{ ...styles.subheading, color: accentTheme.heading }}>
            {selectPdfValue(config.language, {
              en: 'Standards and references',
              pl: 'Standardy i odniesienia',
              sv: 'Standarder och referenser',
            })}
          </Text>
          {copy.methodologyReferenceLines.map((line) => (
            <Text key={line} style={styles.listItem}>
              • {line}
            </Text>
          ))}
          <Text style={styles.richTextParagraph}>
            {getEcoinventReference(config.language, ecoinventVersion)}
          </Text>
          <Text style={styles.richTextParagraph}>
            {copy.methodologyLimitationText}
          </Text>
          <Text style={{ ...styles.subheading, color: accentTheme.heading }}>
            {selectPdfValue(config.language, {
              en: 'Study-specific inputs',
              pl: 'Dane specyficzne dla badania',
              sv: 'Studieunika indata',
            })}
          </Text>
          <View style={styles.row}>
            <Text style={styles.label}>
              {selectPdfValue(config.language, {
                en: 'Intended applications',
                pl: 'Zamierzone zastosowania',
                sv: 'Avsedda användningar',
              })}
            </Text>
            <Text style={styles.value}>
              {product?.goals?.intended_applications || copy.notProvided}
            </Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>
              {selectPdfValue(config.language, {
                en: 'Study reasons',
                pl: 'Powody badania',
                sv: 'Skäl för studien',
              })}
            </Text>
            <Text style={styles.value}>
              {product?.goals?.study_reasons || copy.notProvided}
            </Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>
              {selectPdfValue(config.language, {
                en: 'Target audience',
                pl: 'Grupa docelowa',
                sv: 'Målgrupp',
              })}
            </Text>
            <Text style={styles.value}>
              {formatTargetAudience(
                product?.goals?.target_audience || '',
                (optionId: TargetAudienceOptionId) =>
                  tLca(
                    config.language,
                    `goals.targetAudienceOptions.${optionId}`
                  )
              ) || copy.notProvided}
            </Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>
              {selectPdfValue(config.language, {
                en: 'Commissioner',
                pl: 'Zleceniodawca',
                sv: 'Beställare',
              })}
            </Text>
            <Text style={styles.value}>
              {product?.goals?.comissioner_name || copy.notProvided}
            </Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>
              {selectPdfValue(config.language, {
                en: 'Regulatory frameworks',
                pl: 'Ramy regulacyjne',
                sv: 'Regelverk',
              })}
            </Text>
            <Text style={styles.value}>
              {product?.regulatory_frameworks || copy.notProvided}
            </Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>
              {selectPdfValue(config.language, {
                en: 'Product category rules',
                pl: 'Zasady kategorii produktu',
                sv: 'Produktkategoriregler',
              })}
            </Text>
            <Text style={styles.value}>
              {product?.product_category_rules || copy.notProvided}
            </Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>
              {selectPdfValue(config.language, {
                en: 'Cut-off rules',
                pl: 'Zasady cut-off',
                sv: 'Cut-off-regler',
              })}
            </Text>
            <Text style={styles.value}>
              {product?.cutoff_rules || copy.notProvided}
            </Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>
              {selectPdfValue(config.language, {
                en: 'Ecoinvent version',
                pl: 'Wersja ecoinvent',
                sv: 'Ecoinvent-version',
              })}
            </Text>
            <Text style={styles.value}>
              {ecoinventVersion || copy.notProvided}
            </Text>
          </View>
          {product?.assumptions_description && (
            <>
              <Text
                style={{ ...styles.subheading, color: accentTheme.heading }}
              >
                {selectPdfValue(config.language, {
                  en: 'Assumptions',
                  pl: 'Założenia',
                  sv: 'Antaganden',
                })}
              </Text>
              <Text>{product.assumptions_description}</Text>
            </>
          )}
          {product?.production_process_description && (
            <>
              <Text
                style={{ ...styles.subheading, color: accentTheme.heading }}
              >
                {selectPdfValue(config.language, {
                  en: 'Production process context',
                  pl: 'Kontekst procesu produkcyjnego',
                  sv: 'Sammanhang för produktionsprocessen',
                })}
              </Text>
              <Text>{product.production_process_description}</Text>
            </>
          )}
        </>
      );
    }

    if (section.id === 'materials-circularity') {
      if (!product) {
        return <Text style={styles.neutralBox}>{copy.dataNotAvailable}</Text>;
      }

      return (
        <>
          <View style={styles.row}>
            <Text style={styles.label}>
              {selectPdfValue(config.language, {
                en: 'Materials captured',
                pl: 'Zebrane materiały',
                sv: 'Registrerade material',
              })}
            </Text>
            <Text style={styles.value}>{product.materials.length}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>
              {selectPdfValue(config.language, {
                en: 'Weighted recycled share',
                pl: 'Ważony udział recyklatu',
                sv: 'Viktad andel återvunnet material',
              })}
            </Text>
            <Text style={styles.value}>
              {product.materials.length
                ? `${getWeightedRecycledShare(product).toFixed(1)}%`
                : copy.notProvided}
            </Text>
          </View>
          {materialRows.map((material) => (
            <View style={styles.row} key={material.material_name}>
              <Text style={styles.label}>{material.material_name}</Text>
              <Text style={styles.value}>
                {selectPdfValue(config.language, {
                  en: `${material.percentage.toFixed(1)}% | recycled ${material.recycled_share_pct.toFixed(1)}%`,
                  pl: `${material.percentage.toFixed(1)}% | recyklat ${material.recycled_share_pct.toFixed(1)}%`,
                  sv: `${material.percentage.toFixed(1)}% | återvunnet ${material.recycled_share_pct.toFixed(1)}%`,
                })}
              </Text>
            </View>
          ))}
          <Text style={{ ...styles.subheading, color: accentTheme.heading }}>
            {selectPdfValue(config.language, {
              en: 'Circularity notes',
              pl: 'Notatki o cyrkularności',
              sv: 'Anteckningar om cirkularitet',
            })}
          </Text>
          {[
            [
              selectPdfValue(config.language, {
                en: 'Recycled materials share',
                pl: 'Udział materiałów z recyklingu',
                sv: 'Andel återvunnet material',
              }),
              product.recycled_materials_share,
            ],
            [
              selectPdfValue(config.language, {
                en: 'Renewable materials content',
                pl: 'Zawartość materiałów odnawialnych',
                sv: 'Innehåll av förnybara material',
              }),
              product.renewable_materials_content,
            ],
            [
              selectPdfValue(config.language, {
                en: 'Material recovery / critical raw materials',
                pl: 'Odzysk materiałów / surowce krytyczne',
                sv: 'Materialåtervinning / kritiska råvaror',
              }),
              product.material_recovery_critical_raw_materials,
            ],
            [
              selectPdfValue(config.language, {
                en: 'Recycling possibilities',
                pl: 'Możliwości recyklingu',
                sv: 'Återvinningsmöjligheter',
              }),
              product.recycling_possibilities,
            ],
            [
              selectPdfValue(config.language, {
                en: 'Disassembly instructions',
                pl: 'Instrukcje demontażu',
                sv: 'Demonteringsinstruktioner',
              }),
              product.disassembly_instructions,
            ],
            [
              selectPdfValue(config.language, {
                en: 'EPR information',
                pl: 'Informacje EPR',
                sv: 'EPR-information',
              }),
              product.epr_information_description,
            ],
          ]
            .filter(([, value]) => Boolean(value))
            .map(([label, value]) => (
              <Text key={label} style={styles.listItem}>
                • {label}: {value}
              </Text>
            ))}
        </>
      );
    }

    if (section.id === 'durability-end-of-life') {
      if (!product) {
        return <Text style={styles.neutralBox}>{copy.dataNotAvailable}</Text>;
      }

      return (
        <>
          {[
            [
              selectPdfValue(config.language, {
                en: 'Reference service life',
                pl: 'Referencyjny okres użytkowania',
                sv: 'Referenslivslängd',
              }),
              product.reference_service_life,
            ],
            [
              selectPdfValue(config.language, {
                en: 'Guaranteed service life',
                pl: 'Gwarantowany okres użytkowania',
                sv: 'Garanterad livslängd',
              }),
              product.guaranteed_service_life,
            ],
            [
              selectPdfValue(config.language, {
                en: 'Operating life',
                pl: 'Okres eksploatacji',
                sv: 'Driftlivslängd',
              }),
              product.operating_life,
            ],
            [
              selectPdfValue(config.language, {
                en: 'Mean time between failures',
                pl: 'Średni czas między awariami',
                sv: 'Medeltid mellan fel',
              }),
              product.mean_time_between_failures,
            ],
            [
              selectPdfValue(config.language, {
                en: 'Availability of spare parts',
                pl: 'Dostępność części zamiennych',
                sv: 'Tillgång till reservdelar',
              }),
              product.availability_of_spare_parts,
            ],
          ].map(([label, value]) => (
            <View style={styles.row} key={label}>
              <Text style={styles.label}>{label}</Text>
              <Text style={styles.value}>{value || copy.notProvided}</Text>
            </View>
          ))}
          <Text style={{ ...styles.subheading, color: accentTheme.heading }}>
            {selectPdfValue(config.language, {
              en: 'Use and recovery guidance',
              pl: 'Wskazówki użytkowania i odzysku',
              sv: 'Vägledning för användning och återvinning',
            })}
          </Text>
          {[
            [
              selectPdfValue(config.language, {
                en: 'Repair / disassembly instructions',
                pl: 'Instrukcje naprawy / demontażu',
                sv: 'Instruktioner för reparation / demontering',
              }),
              product.repair_disassembly_instructions_description,
            ],
            [
              selectPdfValue(config.language, {
                en: 'Actual product use information',
                pl: 'Informacje o rzeczywistym użytkowaniu produktu',
                sv: 'Information om faktisk produktanvändning',
              }),
              product.actual_product_use_information,
            ],
            [
              selectPdfValue(config.language, {
                en: 'Resistance to stress or ageing mechanisms',
                pl: 'Odporność na obciążenia lub mechanizmy starzenia',
                sv: 'Motståndskraft mot belastning eller åldringsmekanismer',
              }),
              product.resistance_to_stress_or_aging_mechanisms,
            ],
            [
              selectPdfValue(config.language, {
                en: 'Recycling possibilities',
                pl: 'Możliwości recyklingu',
                sv: 'Återvinningsmöjligheter',
              }),
              product.recycling_possibilities,
            ],
          ]
            .filter(([, value]) => Boolean(value))
            .map(([label, value]) => (
              <Text key={label} style={styles.listItem}>
                • {label}: {value}
              </Text>
            ))}
        </>
      );
    }

    if (section.id === 'data-quality-readiness') {
      return (
        <>
          <Text style={styles.leadText}>
            {selectPdfValue(config.language, {
              en: 'This section shows how much platform content is available to support the report narrative and downstream passport outputs.',
              pl: 'Ta sekcja pokazuje, jak dużo treści z platformy jest dostępne, aby wesprzeć narrację raportu i dalsze wyjścia, takie jak paszport produktu.',
              sv: 'Det här avsnittet visar hur mycket plattformsinnehåll som finns tillgängligt för att stödja rapportberättelsen och efterföljande passutdata.',
            })}
          </Text>
          {readinessMetrics.map((metric) => (
            <View style={styles.row} key={metric.label}>
              <Text style={styles.label}>{metric.label}</Text>
              <Text style={styles.value}>
                {metric.filled}/{metric.total} ({Math.round(metric.percentage)}
                %)
              </Text>
            </View>
          ))}
          {readinessHighlights.length > 0 && (
            <>
              <Text
                style={{ ...styles.subheading, color: accentTheme.heading }}
              >
                {selectPdfValue(config.language, {
                  en: 'Gaps worth filling next',
                  pl: 'Luki, które warto uzupełnić',
                  sv: 'Luckor som är värda att fylla härnäst',
                })}
              </Text>
              {readinessHighlights.map((item) => (
                <Text key={item} style={styles.listItem}>
                  • {item}
                </Text>
              ))}
            </>
          )}
        </>
      );
    }

    if (section.id === 'system-boundaries') {
      const includeFlowchart =
        sectionConfig?.options.includeFlowchart !== false;
      const includeImpactValues =
        sectionConfig?.options.includeImpactValues !== false;
      const groupedFlowchartStages = {
        cradle: flowchartStages.filter((stage) => stage.group === 'cradle'),
        grave: flowchartStages.filter((stage) => stage.group === 'grave'),
      };

      return (
        <>
          <View style={styles.row}>
            <Text style={styles.label}>
              {selectPdfValue(config.language, {
                en: 'Lifecycle boundary',
                pl: 'Granica cyklu życia',
                sv: 'Livscykelgräns',
              })}
            </Text>
            <Text style={styles.value}>
              {getLifecycleBoundaryLabel(
                product?.lifecycle_boundaries,
                config.language
              )}
            </Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>
              {selectPdfValue(config.language, {
                en: 'Distribution regions',
                pl: 'Regiony dystrybucji',
                sv: 'Distributionsregioner',
              })}
            </Text>
            <Text style={styles.value}>
              {product?.distribution_regions?.length
                ? product.distribution_regions
                    .map((region) => region.name || region.iso_code)
                    .join(', ')
                : copy.notProvided}
            </Text>
          </View>
          {stageRows.map((stage) => (
            <View style={styles.row} key={stage.key}>
              <Text style={styles.label}>
                {stage.code} ({stage.name})
              </Text>
              <Text style={styles.value}>
                {formatNumber(
                  stage.value,
                  config.layout.numberFormat,
                  config.language
                )}
                {config.layout.numberFormat.showUnits &&
                previewData.selectedImpact
                  ? ` ${previewData.selectedImpact.area.unit_name}`
                  : ''}
              </Text>
            </View>
          ))}
          {!!stageMethodologyRows.length && (
            <>
              <Text
                style={{ ...styles.subheading, color: accentTheme.heading }}
              >
                {selectPdfValue(config.language, {
                  en: 'Life cycle stage descriptions',
                  pl: 'Opisy etapów cyklu życia',
                  sv: 'Beskrivningar av livscykelstadier',
                })}
              </Text>
              {stageMethodologyRows.map((stage) => (
                <Text key={stage.key} style={styles.listItem}>
                  • {stage.code} ({stage.name}): {stage.description}
                </Text>
              ))}
            </>
          )}
          {includeFlowchart && !!flowchartStages.length && (
            <View style={styles.sectionCard}>
              <Text
                style={{ ...styles.subheading, color: accentTheme.heading }}
              >
                {selectPdfValue(config.language, {
                  en: 'Platform flowchart snapshot',
                  pl: 'Migawka flowchartu z platformy',
                  sv: 'Ögonblicksbild av plattformens flödesschema',
                })}
              </Text>
              <Text style={styles.leadText}>
                {selectPdfValue(config.language, {
                  en: 'Print-friendly snapshot of the configured flowchart. Each stage card keeps the platform structure visible and can optionally show the selected impact result next to the stage.',
                  pl: 'Migawka skonfigurowanego flowchartu przyjazna do wydruku. Każda karta etapu zachowuje strukturę platformy i może opcjonalnie pokazywać wynik wybranego wpływu obok etapu.',
                  sv: 'Utskriftsvänlig ögonblicksbild av det konfigurerade flödesschemat. Varje etappkort behåller plattformens struktur synlig och kan valfritt visa resultatet för den valda påverkan bredvid etappen.',
                })}
              </Text>

              {(['cradle', 'grave'] as const).map((groupKey) =>
                groupedFlowchartStages[groupKey].length ? (
                  <View key={groupKey}>
                    <Text
                      style={{
                        ...styles.flowchartGroupTitle,
                        color: accentTheme.heading,
                      }}
                    >
                      {getFlowchartGroupTitle(groupKey)}
                    </Text>
                    <View style={styles.flowchartGrid}>
                      {groupedFlowchartStages[groupKey].map((stage) => (
                        <View key={stage.key} style={styles.flowchartCard}>
                          <Text style={styles.flowchartStageCode}>
                            {stage.code}
                          </Text>
                          <Text
                            style={{
                              ...styles.flowchartStageName,
                              color: accentTheme.heading,
                            }}
                          >
                            {stage.name}
                          </Text>
                          <Text style={styles.flowchartMeta}>
                            {selectPdfValue(config.language, {
                              en: `${stage.nodeCount} mapped objects/processes`,
                              pl: `${stage.nodeCount} przypisanych obiektów/procesów`,
                              sv: `${stage.nodeCount} mappade objekt/processer`,
                            })}
                          </Text>
                          {!!stage.topLabels.length && (
                            <Text style={styles.flowchartMeta}>
                              {selectPdfValue(config.language, {
                                en: 'Top items: ',
                                pl: 'Najważniejsze elementy: ',
                                sv: 'Viktigaste poster: ',
                              })}
                              {stage.topLabels.join(', ')}
                            </Text>
                          )}
                          {!stage.topLabels.length && (
                            <Text style={styles.flowchartMeta}>
                              {selectPdfValue(config.language, {
                                en: 'No stage items mapped yet',
                                pl: 'Brak jeszcze przypisanych elementów etapu',
                                sv: 'Inga etapposter har mappats ännu',
                              })}
                            </Text>
                          )}
                          {includeImpactValues &&
                            previewData.selectedImpact && (
                              <Text style={styles.flowchartMeta}>
                                {selectPdfValue(config.language, {
                                  en: 'Impact value: ',
                                  pl: 'Wartość wpływu: ',
                                  sv: 'Påverkansvärde: ',
                                })}
                                {formatNumber(
                                  stage.impactValue,
                                  config.layout.numberFormat,
                                  config.language
                                )}{' '}
                                {previewData.selectedImpact.area.unit_name}
                              </Text>
                            )}
                        </View>
                      ))}
                    </View>
                  </View>
                ) : null
              )}
            </View>
          )}
        </>
      );
    }

    if (section.id === 'results-ghg') {
      if (!stageRows.length) {
        return (
          <Text style={styles.neutralBox}>
            {copy.dataNotAvailableSelectedScope}
          </Text>
        );
      }

      return (
        <>
          <Text style={styles.leadText}>
            {selectPdfValue(config.language, {
              en: 'Stage-level split for the selected impact area, ordered according to the life-cycle flow.',
              pl: 'Podział wyników według etapów dla wybranego obszaru wpływu, uporządkowany zgodnie z przebiegiem cyklu życia.',
              sv: 'Etappvis uppdelning av resultaten för det valda påverkansområdet, ordnad enligt livscykelflödet.',
            })}
          </Text>
          <View style={styles.sectionCard}>
            <Text style={styles.metricLabel}>
              {selectPdfValue(config.language, {
                en: 'Selected impact area',
                pl: 'Wybrany obszar wpływu',
                sv: 'Valt påverkansområde',
              })}
            </Text>
            <Text style={{ ...styles.metricValue, color: accentTheme.heading }}>
              {previewData.selectedImpact?.area.name}
            </Text>
            <Text style={styles.metricSubvalue}>
              {selectPdfValue(config.language, {
                en: 'Unit: ',
                pl: 'Jednostka: ',
                sv: 'Enhet: ',
              })}
              {previewData.selectedImpact?.area.unit_name}
            </Text>
          </View>
          {sectionConfig?.options.includeCharts !== false &&
            renderStageDistributionChart({
              config,
              rows: stageRows,
              total: previewData.selectedImpact?.total_emission || 0,
              title: selectPdfValue(config.language, {
                en: 'Numerical result chart',
                pl: 'Wykres wyniku liczbowego',
                sv: 'Diagram över numeriskt resultat',
              }),
              unit: previewData.selectedImpact?.area.unit_name,
            })}
          {stageRows.map((stage) => (
            <View style={styles.row} key={stage.key}>
              <Text style={styles.label}>
                {stage.code} ({stage.name})
              </Text>
              <Text style={styles.value}>
                {formatNumber(
                  stage.value,
                  config.layout.numberFormat,
                  config.language
                )}
                {config.layout.numberFormat.showUnits &&
                previewData.selectedImpact
                  ? ` ${previewData.selectedImpact.area.unit_name}`
                  : ''}
              </Text>
            </View>
          ))}
          {sectionConfig?.options.includeHotspots !== false &&
            !!previewData.hotspots.length && (
              <View style={styles.hotspotCard}>
                <Text
                  style={{ ...styles.subheading, color: accentTheme.heading }}
                >
                  {selectPdfValue(config.language, {
                    en: 'Dominant stages',
                    pl: 'Dominujące etapy',
                    sv: 'Dominerande stadier',
                  })}
                </Text>
                {previewData.hotspots.map((hotspot) => (
                  <Text key={hotspot.stageCode} style={styles.listItem}>
                    {selectPdfValue(config.language, {
                      en: `• ${hotspot.stageCode} (${getLocalizedHotspotName(
                        hotspot.stageKey,
                        hotspot.name,
                        config.language
                      )}) contributes ${hotspot.percentage.toFixed(1)}% of the selected impact.`,
                      pl: `• ${hotspot.stageCode} (${getLocalizedHotspotName(
                        hotspot.stageKey,
                        hotspot.name,
                        config.language
                      )}) odpowiada za ${hotspot.percentage.toFixed(1)}% wybranego wpływu.`,
                      sv: `• ${hotspot.stageCode} (${getLocalizedHotspotName(
                        hotspot.stageKey,
                        hotspot.name,
                        config.language
                      )}) står för ${hotspot.percentage.toFixed(1)}% av den valda påverkan.`,
                    })}
                  </Text>
                ))}
              </View>
            )}
        </>
      );
    }

    if (section.id === 'impact-categories') {
      if (
        !selectedImpactCategories.length &&
        !additionalEnvironmentalRows.length
      ) {
        return (
          <Text style={styles.neutralBox}>
            {copy.dataNotAvailableSelectedScope}
          </Text>
        );
      }
      return (
        <>
          {selectedImpactCategories.map((impact) => {
            const impactStageRows = getOrderedStageEntries(
              impact.emission_per_stage,
              config.language
            );

            return (
              <View key={impact.area.id} style={styles.impactCard}>
                <Text style={styles.metricLabel}>
                  {selectPdfValue(config.language, {
                    en: 'Environmental impact',
                    pl: 'Wpływ środowiskowy',
                    sv: 'Miljöpåverkan',
                  })}
                </Text>
                <Text
                  style={{ ...styles.metricValue, color: accentTheme.heading }}
                >
                  {impact.area.name}
                </Text>
                <Text style={styles.metricSubvalue}>
                  {selectPdfValue(config.language, {
                    en: 'Total: ',
                    pl: 'Suma: ',
                    sv: 'Totalt: ',
                  })}
                  {formatNumber(
                    impact.total_emission,
                    config.layout.numberFormat,
                    config.language
                  )}{' '}
                  {impact.area.unit_name}
                </Text>

                {sectionConfig?.options.includeCharts !== false &&
                  renderStageDistributionChart({
                    config,
                    rows: impactStageRows,
                    total: impact.total_emission || 0,
                    title: selectPdfValue(config.language, {
                      en: `Stage profile for ${impact.area.name}`,
                      pl: `Profil etapów dla ${impact.area.name}`,
                      sv: `Etapprofil för ${impact.area.name}`,
                    }),
                    unit: impact.area.unit_name,
                  })}

                {impactStageRows.map((stage) => (
                  <View
                    style={styles.row}
                    key={`${impact.area.id}-${stage.key}`}
                  >
                    <Text style={styles.label}>
                      {stage.code} ({stage.name})
                    </Text>
                    <Text style={styles.value}>
                      {formatNumber(
                        stage.value,
                        config.layout.numberFormat,
                        config.language
                      )}{' '}
                      {impact.area.unit_name}
                    </Text>
                  </View>
                ))}
              </View>
            );
          })}

          {sectionConfig?.options.includeAdditionalEnvironmentalData !==
            false &&
            additionalEnvironmentalRows.length > 0 && (
              <View style={styles.sectionCard}>
                <Text
                  style={{ ...styles.subheading, color: accentTheme.heading }}
                >
                  {selectPdfValue(config.language, {
                    en: 'Additional environmental declarations',
                    pl: 'Dodatkowe deklaracje środowiskowe',
                    sv: 'Ytterligare miljödeklarationer',
                  })}
                </Text>
                {additionalEnvironmentalRows.map((row) => (
                  <View style={styles.row} key={row.label}>
                    <Text style={styles.label}>{row.label}</Text>
                    <Text style={styles.value}>{row.value}</Text>
                  </View>
                ))}
              </View>
            )}
        </>
      );
    }

    if (section.id === 'interpretation') {
      const interpretationSection = getSection(config, 'interpretation');
      const narrative =
        (section.detailLevel !== 'summary' &&
          typeof interpretationSection?.options?.narrative === 'string' &&
          interpretationSection.options.narrative) ||
        selectPdfValue(config.language, {
          en: 'Hotspots and total footprint interpreted at summary level.',
          pl: 'Hotspoty i łączny ślad zinterpretowano na poziomie podsumowania.',
          sv: 'Fokusområden och totalt avtryck har tolkats på sammanfattningsnivå.',
        });

      return (
        <>
          <Text style={styles.leadText}>{narrative}</Text>
          <Text style={styles.listItem}>
            {selectPdfValue(config.language, {
              en: '• The dominant impacts should be reviewed first for reduction opportunities and data-quality improvement.',
              pl: '• Dominujące wpływy należy przeanalizować w pierwszej kolejności pod kątem możliwości redukcji i poprawy jakości danych.',
              sv: '• De dominerande påverkanstyperna bör granskas först för att hitta minskningsmöjligheter och förbättra datakvaliteten.',
            })}
          </Text>
          <Text style={styles.listItem}>
            {selectPdfValue(config.language, {
              en: '• Richer platform content improves how clearly the report explains assumptions, circularity, and use-phase behavior.',
              pl: '• Bogatsza zawartość platformy poprawia przejrzystość wyjaśnienia założeń, cyrkularności i zachowania w fazie użytkowania.',
              sv: '• Rikare plattformsinnehåll gör det tydligare hur rapporten förklarar antaganden, cirkularitet och beteende i användningsfasen.',
            })}
          </Text>
        </>
      );
    }

    if (section.id === 'bibliography') {
      return (
        <>
          <View style={styles.row}>
            <Text style={styles.label}>
              {selectPdfValue(config.language, {
                en: 'Ecoinvent version',
                pl: 'Wersja ecoinvent',
                sv: 'Ecoinvent-version',
              })}
            </Text>
            <Text style={styles.value}>
              {product?.ecoinvent_version || copy.notProvided}
            </Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>
              {selectPdfValue(config.language, {
                en: 'Regulatory frameworks',
                pl: 'Ramy regulacyjne',
                sv: 'Regelverk',
              })}
            </Text>
            <Text style={styles.value}>
              {product?.regulatory_frameworks || copy.notProvided}
            </Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>
              {selectPdfValue(config.language, {
                en: 'Product category rules',
                pl: 'Zasady kategorii produktu',
                sv: 'Produktkategoriregler',
              })}
            </Text>
            <Text style={styles.value}>
              {product?.product_category_rules || copy.notProvided}
            </Text>
          </View>
          {product?.technical_documentation_description && (
            <Text style={styles.listItem}>
              {selectPdfValue(config.language, {
                en: '• Technical documentation: ',
                pl: '• Dokumentacja techniczna: ',
                sv: '• Teknisk dokumentation: ',
              })}
              {product.technical_documentation_description}
            </Text>
          )}
          {product?.development_flowcharts_description && (
            <Text style={styles.listItem}>
              {selectPdfValue(config.language, {
                en: '• Development flowcharts: ',
                pl: '• Flowcharty rozwojowe: ',
                sv: '• Utvecklingsflödesscheman: ',
              })}
              {product.development_flowcharts_description}
            </Text>
          )}
          {product?.related_documents_description && (
            <Text style={styles.listItem}>
              {selectPdfValue(config.language, {
                en: `• Related documents: ${product.related_documents_description}`,
                pl: `• Powiązane dokumenty: ${product.related_documents_description}`,
                sv: `• Relaterade dokument: ${product.related_documents_description}`,
              })}
            </Text>
          )}
        </>
      );
    }

    if (section.id === 'disclaimer-audit-trail') {
      return (
        <>
          <View style={styles.row}>
            <Text style={styles.label}>
              {selectPdfValue(config.language, {
                en: 'Report ID',
                pl: 'ID raportu',
                sv: 'Rapport-ID',
              })}
            </Text>
            <Text style={styles.value}>{config.reportId}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>
              {selectPdfValue(config.language, {
                en: 'Hash',
                pl: 'Hash',
                sv: 'Hash',
              })}
            </Text>
            <Text style={styles.value}>
              {config.audit.hash ||
                selectPdfValue(config.language, {
                  en: 'Not generated',
                  pl: 'Nie wygenerowano',
                  sv: 'Inte genererat',
                })}
            </Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>
              {selectPdfValue(config.language, {
                en: 'Model version',
                pl: 'Wersja modelu',
                sv: 'Modellversion',
              })}
            </Text>
            <Text style={styles.value}>{config.audit.modelVersion}</Text>
          </View>
        </>
      );
    }

    if (section.id === 'scenarios') {
      return (
        <Text style={styles.neutralBox}>
          {selectPdfValue(config.language, {
            en: 'No scenario data available. Section intentionally omitted from final report.',
            pl: 'Brak dostępnych danych scenariuszowych. Sekcja została celowo pominięta w raporcie końcowym.',
            sv: 'Inga scenariodata finns tillgängliga. Avsnittet har medvetet utelämnats från slutrapporten.',
          })}
        </Text>
      );
    }

    return (
      <Text>
        {getReportSectionDescription(section.id, config.language) || ''}
      </Text>
    );
  };

  const sectionIssues = issues.filter(
    (issue) => issue.sectionId === section.id
  );
  const hasDataGap = sectionHasDataGap(section.id, issues);

  return (
    <Page size="A4" style={styles.page}>
      {isDraft && (
        <Text style={styles.watermark} fixed>
          {copy.draft}
        </Text>
      )}
      <Text style={{ ...styles.sectionTitle, color: accentTheme.heading }}>
        {getReportSectionTitle(section.id, config.language)}
      </Text>
      {renderSectionContent()}
      {(customContent.html || customContent.images.length > 0) && (
        <View style={styles.customContentWrap}>
          {renderRichTextPdf(customContent.html)}
          {customContent.images.map((image) => (
            <View
              key={image.id}
              style={{
                ...styles.customImageWrap,
                width: getPdfImageWidth(image.layout),
              }}
            >
              <Image src={image.url} style={styles.customImage} />
              {(image.caption || image.alt) && (
                <Text style={styles.customImageCaption}>
                  {image.caption || image.alt}
                </Text>
              )}
            </View>
          ))}
        </View>
      )}
      {hasDataGap && (
        <Text style={styles.chip}>
          {selectPdfValue(config.language, {
            en: 'Data gap: missing optional values were replaced with Not provided.',
            pl: 'Luka danych: brakujące wartości opcjonalne zostały zastąpione tekstem „Nie podano”.',
            sv: 'Datalucka: saknade valfria värden ersattes med texten ”Inte angivet”.',
          })}
        </Text>
      )}
      {sectionIssues.map((issue) => (
        <Text key={issue.id} style={styles.warning}>
          {issue.message}
        </Text>
      ))}
      {renderFooter(config, pageNumber)}
    </Page>
  );
};

const OnePagerPage = ({
  config,
  previewData,
  product,
  isDraft,
}: {
  config: ReportConfig;
  previewData: BuilderPreviewData;
  product: ReportProductData | null;
  isDraft: boolean;
}) => {
  const copy = getPdfCopy(config.language);
  const orderedImpacts = [
    previewData.selectedImpact,
    ...previewData.availableImpacts.filter(
      (impact) => impact.area.id !== previewData.selectedImpact?.area.id
    ),
  ].filter(
    (impact, index, array): impact is NonNullable<typeof impact> =>
      Boolean(impact) &&
      array.findIndex((item) => item?.area.id === impact?.area.id) === index
  );

  const onePagerImpacts = orderedImpacts.slice(0, 4);
  const stageRows = getOrderedStageEntries(
    previewData.selectedImpact?.emission_per_stage,
    config.language
  ).slice(0, 4);
  const productImageUrl = toAbsoluteAssetUrl(product?.image_url);
  const producerLogoUrl = toAbsoluteAssetUrl(config.branding.logoUrl);
  const accentTheme = getReportAccentTheme(config);
  const ecoinventVersion =
    product?.ecoinvent_version ||
    config.audit.datasetVersions.ecoinvent ||
    null;
  const requirements = [
    [
      selectPdfValue(config.language, {
        en: 'Regulatory frameworks',
        pl: 'Ramy regulacyjne',
        sv: 'Regelverk',
      }),
      product?.regulatory_frameworks,
    ],
    [
      selectPdfValue(config.language, {
        en: 'Product category rules',
        pl: 'Zasady kategorii produktu',
        sv: 'Produktkategoriregler',
      }),
      product?.product_category_rules,
    ],
    [
      selectPdfValue(config.language, {
        en: 'Lifecycle boundary',
        pl: 'Granica cyklu życia',
        sv: 'Livscykelgräns',
      }),
      getLifecycleBoundaryLabel(product?.lifecycle_boundaries, config.language),
    ],
    [
      selectPdfValue(config.language, {
        en: 'Data collection period',
        pl: 'Okres zbierania danych',
        sv: 'Datainsamlingsperiod',
      }),
      product?.data_collection_period,
    ],
    [
      selectPdfValue(config.language, {
        en: 'Ecoinvent version',
        pl: 'Wersja ecoinvent',
        sv: 'Ecoinvent-version',
      }),
      ecoinventVersion,
    ],
  ].filter(([, value]) => Boolean(value)) as Array<[string, string]>;
  const summaryLines = buildOnePagerSummary({
    product,
    previewData,
    visibleImpactsCount: onePagerImpacts.length,
    language: config.language,
  });
  const onePagerMethodologyNote = truncateText(
    selectPdfValue(config.language, {
      en: `Background modelling may use ecoinvent datasets${
        ecoinventVersion ? ` (version ${ecoinventVersion})` : ''
      }. Results should be read as decision-support information for hotspot identification and improvement planning.`,
      pl: `Modelowanie tła może wykorzystywać zbiory danych ecoinvent${
        ecoinventVersion ? ` (wersja ${ecoinventVersion})` : ''
      }. Wyniki należy traktować jako wsparcie decyzji przy identyfikacji hotspotów i planowaniu usprawnień.`,
      sv: `Bakgrundsmodelleringen kan använda ecoinvent-dataset${
        ecoinventVersion ? ` (version ${ecoinventVersion})` : ''
      }. Resultaten bör läsas som beslutsstöd för att identifiera fokusområden och planera förbättringar.`,
    }),
    180
  );
  const basicInfo = [
    [
      selectPdfValue(config.language, {
        en: 'Manufacturer',
        pl: 'Producent',
        sv: 'Tillverkare',
      }),
      product?.manufacturer_name || config.branding.companyNameOverride,
    ],
    [
      selectPdfValue(config.language, {
        en: 'Functional unit',
        pl: 'Jednostka funkcjonalna',
        sv: 'Funktionell enhet',
      }),
      product?.functional_unit || copy.notProvided,
    ],
    [
      selectPdfValue(config.language, {
        en: 'Weight',
        pl: 'Waga',
        sv: 'Vikt',
      }),
      product?.weight
        ? `${formatNumber(
            product.weight,
            config.layout.numberFormat,
            config.language
          )} ${product.weight_unit || ''}`.trim()
        : copy.notProvided,
    ],
    [
      selectPdfValue(config.language, {
        en: 'Dimensions',
        pl: 'Wymiary',
        sv: 'Mått',
      }),
      product?.dimensions || copy.notProvided,
    ],
  ];
  const shortDescription = truncateText(product?.description, 140);

  return (
    <Page size="A4" style={styles.onePagerPage}>
      {isDraft && (
        <Text style={styles.watermark} fixed>
          {copy.draft}
        </Text>
      )}

      <View
        style={{
          ...styles.onePagerHeader,
          backgroundColor: accentTheme.primary,
        }}
      >
        <View style={styles.onePagerHeaderTop}>
          <View style={styles.onePagerProducerLogoBadge}>
            {producerLogoUrl ? (
              <Image
                src={producerLogoUrl}
                style={styles.onePagerProducerLogo}
              />
            ) : (
              <Text style={styles.onePagerBodyText}>
                {config.branding.companyNameOverride}
              </Text>
            )}
          </View>
          <EnvirlyPdfLogo width={122} height={34} />
        </View>
        <Text style={styles.onePagerLabel}>{copy.onePager.label}</Text>
        <Text style={styles.onePagerTitle}>
          {product?.name || config.branding.productName}
        </Text>
        <Text style={styles.onePagerIntro}>{copy.onePager.intro}</Text>
      </View>

      <View style={styles.onePagerTopRow}>
        <View style={styles.onePagerLeftColumn}>
          <View style={styles.onePagerCard}>
            <Text style={styles.onePagerCardTitle}>
              {copy.onePager.basicProductInformation}
            </Text>
            {basicInfo.map(([label, value]) => (
              <View key={label} style={styles.onePagerInfoRow}>
                <Text style={styles.onePagerInfoLabel}>{label}</Text>
                <Text style={styles.onePagerInfoValue}>{value}</Text>
              </View>
            ))}
            {!!shortDescription && (
              <Text style={styles.onePagerBodyText}>{shortDescription}</Text>
            )}
          </View>
        </View>

        <View style={styles.onePagerRightColumn}>
          <View style={styles.onePagerImageFrame}>
            {productImageUrl ? (
              <Image
                src={productImageUrl}
                style={styles.onePagerProductImage}
              />
            ) : (
              <Text style={styles.onePagerImageFallback}>
                {copy.onePager.imageMissing}
              </Text>
            )}
          </View>
        </View>
      </View>

      <View style={styles.onePagerResultsCard}>
        <Text style={styles.onePagerCardTitle}>
          {copy.onePager.environmentalResults}
        </Text>
        <View style={styles.onePagerResultsGrid}>
          {onePagerImpacts.map((impact, index) => {
            const isPrimary =
              impact.area.id === previewData.selectedImpact?.area.id ||
              index === 0;
            return (
              <View
                key={impact.area.id}
                style={{
                  ...styles.onePagerResultMetric,
                  ...(isPrimary ? styles.onePagerResultMetricPrimary : {}),
                  ...(isPrimary
                    ? { backgroundColor: accentTheme.primary }
                    : { backgroundColor: accentTheme.soft }),
                }}
              >
                <Text
                  style={{
                    ...styles.onePagerResultLabel,
                    ...(isPrimary ? styles.onePagerResultLabelPrimary : {}),
                  }}
                >
                  {impact.area.name}
                </Text>
                <Text
                  style={{
                    ...styles.onePagerResultValue,
                    ...(isPrimary ? styles.onePagerResultValuePrimary : {}),
                  }}
                >
                  {formatNumber(
                    impact.total_emission,
                    config.layout.numberFormat,
                    config.language
                  )}
                </Text>
                <Text
                  style={{
                    ...styles.onePagerResultUnit,
                    ...(isPrimary ? styles.onePagerResultUnitPrimary : {}),
                  }}
                >
                  {impact.area.unit_name}
                </Text>
              </View>
            );
          })}
        </View>

        {renderOnePagerStageChart({
          config,
          rows: stageRows,
          unit: previewData.selectedImpact?.area.unit_name,
        })}
      </View>

      <View style={styles.onePagerBottomRow}>
        <View style={styles.onePagerSummaryCard}>
          <Text style={styles.onePagerCardTitle}>
            {copy.onePager.shortSummary}
          </Text>
          {summaryLines.map((line) => (
            <Text key={line} style={styles.onePagerBullet}>
              • {line}
            </Text>
          ))}
        </View>

        <View style={styles.onePagerRequirementsCard}>
          <Text style={styles.onePagerCardTitle}>
            {copy.onePager.studyFramework}
          </Text>
          {requirements.map(([label, value]) => (
            <View key={label} style={styles.onePagerInfoRow}>
              <Text style={styles.onePagerInfoLabel}>{label}</Text>
              <Text style={styles.onePagerInfoValue}>{value}</Text>
            </View>
          ))}
          {!requirements.length && (
            <Text style={styles.onePagerBodyText}>
              {copy.onePager.studyRequirementsMissing}
            </Text>
          )}
          {!!onePagerMethodologyNote && (
            <Text style={styles.onePagerBodyText}>
              {onePagerMethodologyNote}
            </Text>
          )}
        </View>
      </View>
    </Page>
  );
};

const ClosingPage = ({
  isDraft,
  config,
}: {
  isDraft: boolean;
  config: ReportConfig;
}) => {
  const copy = getPdfCopy(config.language);
  const accentTheme = getReportAccentTheme(config);

  return (
    <Page
      size="A4"
      style={{ ...styles.closingPage, backgroundColor: accentTheme.primary }}
    >
      {isDraft && (
        <Text style={styles.watermark} fixed>
          {copy.draft}
        </Text>
      )}

      <View style={styles.closingLogo}>
        <EnvirlyPdfLogo />
      </View>

      <Text style={styles.closingEyebrow}>{copy.closing.eyebrow}</Text>
      <Text style={styles.closingTitle}>{copy.closing.title}</Text>
      <Text style={styles.closingText}>{copy.closing.body}</Text>

      <View style={styles.closingContactCard}>
        <Text style={styles.closingContactTitle}>{copy.closing.contact}</Text>
        <Text style={styles.closingContactItem}>{ENVIRLY_CONTACT.website}</Text>
        <Text style={styles.closingContactItem}>{ENVIRLY_CONTACT.phone}</Text>
        <Text style={styles.closingContactItem}>{ENVIRLY_CONTACT.email}</Text>
      </View>

      <Text style={styles.closingFooter}>{copy.closing.footer}</Text>
    </Page>
  );
};

export default function LcaReportPdfDocument({
  config,
  previewData,
  diagramData,
  sections,
  issues,
  product,
  isDraft,
}: PdfProps) {
  const copy = getPdfCopy(config.language);
  const accentTheme = getReportAccentTheme(config);
  const showUserCover =
    config.branding.coverStyle === 'user' && !!config.branding.coverUrl;
  const showEnvirlyCover = config.branding.coverStyle === 'envirly';
  const titlePageNumber = showUserCover ? 2 : 1;
  const sectionPageOffset = showUserCover ? 2 : 1;

  if (config.reportType === 'lca-one-pager') {
    return (
      <Document>
        <OnePagerPage
          config={config}
          previewData={previewData}
          product={product}
          isDraft={isDraft}
        />
      </Document>
    );
  }

  return (
    <Document>
      {showUserCover && (
        <Page size="A4" style={styles.titlePage}>
          <Image
            src={config.branding.coverUrl as string}
            style={styles.titleCoverFullPageImage}
          />
        </Page>
      )}

      <Page size="A4" style={styles.page}>
        {isDraft && (
          <Text style={styles.watermark} fixed>
            {copy.draft}
          </Text>
        )}
        {!showUserCover && showEnvirlyCover && (
          <View
            style={{
              ...styles.titleCoverAbstract,
              backgroundColor: accentTheme.primary,
            }}
          >
            <Text style={styles.titleCoverAbstractEyebrow}>
              {selectPdfValue(config.language, {
                en: 'LCA Report',
                pl: 'Raport LCA',
                sv: 'LCA-rapport',
              })}
            </Text>
            <Text style={styles.titleCoverAbstractTitle}>
              {config.branding.productName}
            </Text>
          </View>
        )}
        {config.branding.logoUrl && (
          <Image src={config.branding.logoUrl} style={styles.logo} />
        )}
        <Text style={{ ...styles.title, color: accentTheme.heading }}>
          {config.branding.reportTitle}
        </Text>
        <Text style={styles.subtitle}>{config.branding.productName}</Text>

        <View style={styles.row}>
          <Text style={styles.label}>
            {selectPdfValue(config.language, {
              en: 'Company',
              pl: 'Firma',
              sv: 'Företag',
            })}
          </Text>
          <Text style={styles.value}>
            {config.branding.companyNameOverride}
          </Text>
        </View>
        {config.branding.showDate && (
          <View style={styles.row}>
            <Text style={styles.label}>
              {selectPdfValue(config.language, {
                en: 'Date',
                pl: 'Data',
                sv: 'Datum',
              })}
            </Text>
            <Text style={styles.value}>
              {new Date().toISOString().slice(0, 10)}
            </Text>
          </View>
        )}
        <View style={styles.row}>
          <Text style={styles.label}>
            {selectPdfValue(config.language, {
              en: 'Report type',
              pl: 'Typ raportu',
              sv: 'Rapporttyp',
            })}
          </Text>
          <Text style={styles.value}>
            {getReportTypeLabel(config.reportType, config.language)}
          </Text>
        </View>
        <View style={styles.row}>
          <Text style={styles.label}>
            {selectPdfValue(config.language, {
              en: 'Template',
              pl: 'Szablon',
              sv: 'Mall',
            })}
          </Text>
          <Text style={styles.value}>
            {tReportBuilder(
              config.language,
              `templates.${
                config.templateId === 'audit-ready'
                  ? 'auditReady'
                  : config.templateId
              }`
            )}
          </Text>
        </View>
        <View style={styles.row}>
          <Text style={styles.label}>
            {selectPdfValue(config.language, {
              en: 'Language',
              pl: 'Język',
              sv: 'Språk',
            })}
          </Text>
          <Text style={styles.value}>{config.language.toUpperCase()}</Text>
        </View>

        <Text
          style={{
            marginTop: 12,
            marginBottom: 6,
            color: accentTheme.heading,
          }}
        >
          {selectPdfValue(config.language, {
            en: 'Included sections',
            pl: 'Uwzględnione sekcje',
            sv: 'Inkluderade avsnitt',
          })}
        </Text>
        {sections.map((section) => (
          <Text key={section.id}>
            • {getReportSectionTitle(section.id, config.language)} (
            {selectPdfValue(config.language, {
              en: 'p.',
              pl: 'str.',
              sv: 'sid.',
            })}{' '}
            {section.page + sectionPageOffset})
          </Text>
        ))}

        {renderFooter(config, titlePageNumber)}
      </Page>

      {sections.map((section, index) => (
        <SectionPage
          key={section.id}
          config={config}
          section={section}
          previewData={previewData}
          diagramData={diagramData}
          product={product}
          issues={issues}
          isDraft={isDraft}
          pageNumber={index + sectionPageOffset + 1}
        />
      ))}

      <ClosingPage isDraft={isDraft} config={config} />
    </Document>
  );
}
